import React from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "../../utils/toast";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FlaskConical,
  HelpCircle,
  ListChecks,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Save,
  SlidersHorizontal,
  TestTube,
  Trash2,
  X,
} from "lucide-react";
import {
  createTest,
  deleteTest,
  fetchSampleTypes,
  fetchTestCategories,
  fetchTests,
  updateTest,
} from "../../store/slices/testSlice";
import {
  fetchTestSampleTypesByTest,
} from "../../store/slices/testSampleTypeSlice";
import {
  createTestParameter,
  deleteTestParameter,
  fetchParametersByTest,
  updateTestParameter,
} from "../../store/slices/testParameterSlice";
import {
  createReferenceRange,
  deleteReferenceRange,
  fetchRangesByParameter,
  updateReferenceRange,
} from "../../store/slices/referenceRangeSlice";
import {
  createDynamicQuestion,
  deleteDynamicQuestion,
  fetchQuestionsByTest,
  updateDynamicQuestion,
} from "../../store/slices/dynamicQuestionSlice";
import { fetchTubeTypes } from "../../store/slices/tubeTypeSlice";
import {
  fetchSampleTypeParameters,
  fetchSampleTypeTubeTypes,
  fetchTemplateRanges,
} from "../../store/slices/sampleTypeConfigSlice";

import {
  asString,
  computeCoverageWarnings,
  cx,
  FIELD_TYPE_UI_OPTIONS,
  formatFactorsLines,
  mapUiFieldTypeToDb,
  needsOptionsForDbFieldType,
  normalizeLower,
  parseFactorsLines,
  parseOptionsLines,
  toNumberOrNull,
} from "./testManagement/utils";

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Select,
  Stepper,
  TextArea,
  TextInput,
} from "./testManagement/ui";

const STEPS = [
  { id: "basics", label: "Test Basics", Icon: TestTube },
  { id: "parameters", label: "Parameters", Icon: SlidersHorizontal },
  { id: "ranges", label: "Reference Ranges", Icon: FlaskConical },
  { id: "questions", label: "Dynamic Questions", Icon: ListChecks },
  { id: "confirm", label: "Confirmation", Icon: ClipboardCheck },
];

const TESTING_MODE_OPTIONS = [
  { value: "analyzer", label: "Analyzer-based" },
  { value: "morphology", label: "Morphology" },
  { value: "other", label: "Other" },
];

const normalizeTestingMode = (v) => {
  const x = String(v || "").trim().toLowerCase();
  if (x === "morphology") return "morphology";
  if (x === "other") return "other";
  return "analyzer";
};

const stepsForTestingMode = (testingMode) => {
  const mode = normalizeTestingMode(testingMode);
  if (mode !== "morphology") return STEPS;
  return STEPS.filter((s) => s.id !== "parameters" && s.id !== "ranges");
};

const DATA_TYPES = [
  { value: "numeric", label: "Numeric" },
  { value: "text", label: "Text" },
];

const TestManagement = () => {
  const dispatch = useDispatch();

  const normalizeGender = (v) => {
    const x = normalizeLower(v);
    return x === "male" || x === "female" || x === "both" ? x : "both";
  };

  const normalizePregnancy = (v) => {
    const x = normalizeLower(v);
    return x === "pregnant" || x === "not_pregnant" || x === "both" ? x : "both";
  };

  const unwrapOrToast = async (actionOrPromise, fallbackMessage) => {
    try {
      if (actionOrPromise && typeof actionOrPromise.unwrap === "function") {
        return await actionOrPromise.unwrap();
      }
      return await actionOrPromise;
    } catch (err) {
      const msg =
        fallbackMessage ||
        err?.message ||
        err?.error ||
        "Request failed";
      toast.error(msg);
      return null;
    }
  };

  const { tests, testCategories, sampleTypes = [], loading } = useSelector(
    (s) => s.tests
  );
  const tubeTypes = useSelector((s) => s.tubeTypes.items) || [];
  const testSampleTypesByTest = useSelector((s) => s.testSampleTypes.byTest);
  const parametersByTest = useSelector((s) => s.testParameters.parametersByTest);
  const rangesByParameter = useSelector(
    (s) => s.referenceRanges.rangesByParameter
  );
  const templateParametersBySampleTypeId = useSelector(
    (s) => s.sampleTypeConfig.parametersBySampleTypeId
  );
  const templateRangesByParameterId = useSelector(
    (s) => s.sampleTypeConfig.rangesByParameterId
  );
  const tubeTypeIdsBySampleTypeId = useSelector(
    (s) => s.sampleTypeConfig.tubeTypeIdsBySampleTypeId
  );
  const questionsByTest = useSelector(
    (s) => s.dynamicQuestions.questionsByTest
  );

  const [selectedTestId, setSelectedTestId] = React.useState(null);
  const [stepIndex, setStepIndex] = React.useState(0);

  const [openActionsForTestId, setOpenActionsForTestId] = React.useState(null);
  const [dropdownPosition, setDropdownPosition] = React.useState(null);

  React.useEffect(() => {
    if (!openActionsForTestId) {
      setDropdownPosition(null);
      return;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenActionsForTestId(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [openActionsForTestId]);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState("wizard"); // wizard | view
  const [drawerTestId, setDrawerTestId] = React.useState(null);

  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    confirmTone: "primary",
  });
  const confirmDialogActionRef = React.useRef(null);
  const confirmDialogCancelRef = React.useRef(null);
  const [confirmDialogBusy, setConfirmDialogBusy] = React.useState(false);

  const openConfirmDialog = React.useCallback(
    ({ title, message, confirmText, confirmTone, onConfirm, onCancel }) => {
      confirmDialogActionRef.current = onConfirm || null;
      confirmDialogCancelRef.current = onCancel || null;
      setConfirmDialog({
        open: true,
        title: title || "Confirm",
        message: message || "Are you sure?",
        confirmText: confirmText || "Confirm",
        confirmTone: confirmTone || "primary",
      });
    },
    []
  );

  const closeConfirmDialog = React.useCallback((invokeCancel = true) => {
    if (confirmDialogBusy) return;

    const onCancel = confirmDialogCancelRef.current;
    if (invokeCancel && typeof onCancel === "function") {
      try {
        onCancel();
      } catch {
        // ignore
      }
    }

    confirmDialogActionRef.current = null;
    confirmDialogCancelRef.current = null;
    setConfirmDialog((p) => ({ ...p, open: false }));
  }, [confirmDialogBusy]);

  const confirmAsync = React.useCallback(
    ({ title, message, confirmText, confirmTone }) =>
      new Promise((resolve) => {
        openConfirmDialog({
          title,
          message,
          confirmText,
          confirmTone,
          onConfirm: async () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [openConfirmDialog]
  );

  React.useEffect(() => {
    const onDocClick = () => setOpenActionsForTestId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // UI-only state for calmer editing (keeps existing handlers/dispatches)
  const [expandedSampleTypeIds, setExpandedSampleTypeIds] = React.useState({});
  const [editingParamId, setEditingParamId] = React.useState(null);
  const [paramEditDraftById, setParamEditDraftById] = React.useState({});

  const [selectedRangeSampleTypeId, setSelectedRangeSampleTypeId] = React.useState(null);
  const [rangeEditId, setRangeEditId] = React.useState(null);
  const [rangeEditDraftById, setRangeEditDraftById] = React.useState({});

  const [editingQuestionId, setEditingQuestionId] = React.useState(null);
  const [questionEditDraftById, setQuestionEditDraftById] = React.useState({});

  const [search, setSearch] = React.useState("");

  const [draft, setDraft] = React.useState({
    id: null,
    category_id: "",
    name: "",
    code: "",
    description: "",
    price: "",
    is_active: 0,
    tube_type_id: "",
    processing_time: "",
    testing_mode: "analyzer",
    testing_modes: "",
    sample_volume: "",
    linear_range: "",
    sample_type_ids: [],
  });

  const steps = React.useMemo(
    () => stepsForTestingMode(draft.testing_mode),
    [draft.testing_mode]
  );

  React.useEffect(() => {
    setStepIndex((prev) => Math.max(0, Math.min(prev, steps.length - 1)));
  }, [steps.length]);

  const [confirmPrefetching, setConfirmPrefetching] = React.useState(false);

  const [publishSuccess, setPublishSuccess] = React.useState(false);

  const [parameterDraftByTstId, setParameterDraftByTstId] = React.useState({});

  const [selectedTemplateParamIdsByTstId, setSelectedTemplateParamIdsByTstId] =
    React.useState({});
  const [copyingTemplatesByTstId, setCopyingTemplatesByTstId] =
    React.useState({});

  const [pendingRemovedSampleTypeIds, setPendingRemovedSampleTypeIds] =
    React.useState([]);

  const [selectedParameterId, setSelectedParameterId] = React.useState(null);
  const [rangeDraft, setRangeDraft] = React.useState({
    parameter_id: null,
    gender: "both",
    pregnancy_status: "both",
    age_unit: "years",
    min_age: "",
    max_age: "",
    min_value: "",
    max_value: "",
    critical_low: "",
    critical_high: "",
    text_range: "",
    remarks: "",
    factors: "",
  });

  const [questionDraft, setQuestionDraft] = React.useState({
    question_text: "",
    field_type: "text",
    options: "",
    is_required: 0,
  });

  React.useEffect(() => {
    // Switching to a different test/draft should not keep showing a prior success slide.
    setPublishSuccess(false);
  }, [draft.id]);

  React.useEffect(() => {
    dispatch(fetchTests());
    dispatch(fetchTestCategories());
    dispatch(fetchSampleTypes(true));
    dispatch(fetchTubeTypes(true));
  }, [dispatch]);

  // Prefetch template parameters as soon as sample types are selected so they can be displayed immediately.
  React.useEffect(() => {
    const ids = Array.isArray(draft.sample_type_ids) ? draft.sample_type_ids : [];
    for (const stId of ids) {
      if (!Object.prototype.hasOwnProperty.call(templateParametersBySampleTypeId, stId)) {
        dispatch(fetchSampleTypeParameters(stId));
      }
    }
  }, [dispatch, draft.sample_type_ids, templateParametersBySampleTypeId]);

  // Prefetch tube mappings for selected sample types so Confirmation can show tubes without requiring a page refresh.
  React.useEffect(() => {
    const ids = Array.isArray(draft.sample_type_ids) ? draft.sample_type_ids : [];
    for (const stId of ids) {
      if (!Object.prototype.hasOwnProperty.call(tubeTypeIdsBySampleTypeId, stId)) {
        dispatch(fetchSampleTypeTubeTypes(stId));
      }
    }
  }, [dispatch, draft.sample_type_ids, tubeTypeIdsBySampleTypeId]);

  // Keep the View drawer tube summary fresh.
  React.useEffect(() => {
    if (!drawerTestId) return;
    const ids = Array.from(
      new Set(
        (testSampleTypesByTest[drawerTestId] || [])
          .map((tst) => tst.sample_type_id)
          .filter((x) => x !== null && x !== undefined)
      )
    );
    for (const stId of ids) {
      dispatch(fetchSampleTypeTubeTypes(stId));
    }
  }, [dispatch, drawerTestId, testSampleTypesByTest]);

  const currentStep = steps[stepIndex];

  const currentTest = React.useMemo(() => {
    if (!selectedTestId) return null;
    return tests.find((t) => t.id === selectedTestId) || null;
  }, [selectedTestId, tests]);

  const testSampleTypes = React.useMemo(() => {
    if (!draft.id) return [];
    return testSampleTypesByTest[draft.id] || [];
  }, [draft.id, testSampleTypesByTest]);

  // Prefetch template parameters for each assigned sample type while on Parameters step.
  React.useEffect(() => {
    const isParameters = steps[stepIndex]?.id === "parameters";
    if (!isParameters) return;
    if (!draft.id) return;

    const uniqueSampleTypeIds = Array.from(
      new Set(
        (testSampleTypes || [])
          .map((tst) => tst.sample_type_id)
          .filter((x) => x !== null && x !== undefined)
      )
    );

    for (const stId of uniqueSampleTypeIds) {
      if (
        Object.prototype.hasOwnProperty.call(
          templateParametersBySampleTypeId,
          stId
        )
      ) {
        continue;
      }
      dispatch(fetchSampleTypeParameters(stId));
    }
  }, [
    dispatch,
    draft.id,
    stepIndex,
    testSampleTypes,
    templateParametersBySampleTypeId,
    steps,
  ]);

  // Keep accordion explosion under control (single-open by default)
  React.useEffect(() => {
    const isParameters = steps[stepIndex]?.id === "parameters";
    if (!isParameters) return;
    if (!draft.id) return;
    if (Object.keys(expandedSampleTypeIds).length > 0) return;
    if (testSampleTypes.length === 0) return;
    // Show all sample-type template parameters by default.
    const next = {};
    testSampleTypes.forEach((tst) => {
      next[tst.id] = true;
    });
    setExpandedSampleTypeIds(next);
  }, [draft.id, expandedSampleTypeIds, stepIndex, testSampleTypes, steps]);

  const parameters = React.useMemo(() => {
    if (!draft.id) return [];
    return parametersByTest[draft.id] || [];
  }, [draft.id, parametersByTest]);

  const questions = React.useMemo(() => {
    if (!draft.id) return [];
    return questionsByTest[draft.id] || [];
  }, [draft.id, questionsByTest]);

  const filteredTests = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tests;
    return tests.filter((t) => {
      return (
        String(t.name || "").toLowerCase().includes(q) ||
        String(t.code || "").toLowerCase().includes(q)
      );
    });
  }, [tests, search]);

  const activeTestForMenu = React.useMemo(
    () => filteredTests.find((t) => t.id === openActionsForTestId) || tests.find((t) => t.id === openActionsForTestId) || null,
    [openActionsForTestId, filteredTests, tests]
  );

  const viewTest = React.useMemo(() => {
    if (!drawerTestId) return null;
    return tests.find((t) => t.id === drawerTestId) || null;
  }, [drawerTestId, tests]);

  const viewSampleTypes = React.useMemo(() => {
    if (!drawerTestId) return [];
    return testSampleTypesByTest[drawerTestId] || [];
  }, [drawerTestId, testSampleTypesByTest]);

  const viewParameters = React.useMemo(() => {
    if (!drawerTestId) return [];
    return parametersByTest[drawerTestId] || [];
  }, [drawerTestId, parametersByTest]);

  const viewQuestions = React.useMemo(() => {
    if (!drawerTestId) return [];
    return questionsByTest[drawerTestId] || [];
  }, [drawerTestId, questionsByTest]);

  const getOnlyTstIdForTest = React.useCallback(
    (testId) => {
      if (!testId) return null;
      const rows = testSampleTypesByTest[testId] || [];
      return rows.length === 1 ? rows[0]?.id ?? null : null;
    },
    [testSampleTypesByTest]
  );

  const parameterMatchesTst = React.useCallback(
    (p, tstId, testId) => {
      const v = p?.test_sample_type_id;
      if (v !== null && v !== undefined && v !== "") {
        return String(v) === String(tstId);
      }
      const onlyTstId = getOnlyTstIdForTest(testId);
      return onlyTstId !== null && String(onlyTstId) === String(tstId);
    },
    [getOnlyTstIdForTest]
  );

  const viewIssues = React.useMemo(() => {
    const issues = [];
    if (!drawerTestId) return issues;

    const test = tests.find((t) => t.id === drawerTestId) || null;
    const isMorphology = normalizeTestingMode(test?.testing_mode) === "morphology";

    const tsts = testSampleTypesByTest[drawerTestId] || [];
    if (tsts.length === 0) issues.push("No sample types assigned.");

    if (!isMorphology) {
      const ps = parametersByTest[drawerTestId] || [];
      for (const tst of tsts) {
        const forSampleType = ps.filter((p) => parameterMatchesTst(p, tst.id, drawerTestId));
        if (forSampleType.length === 0) {
          issues.push(`Sample type '${tst.sample_type_name}' has no parameters.`);
        }

        for (const p of forSampleType) {
          const isNumeric = (p.data_type || "numeric") === "numeric";
          if (!isNumeric) continue;
          const ranges = rangesByParameter[p.id] || [];
          if (ranges.length === 0) {
            issues.push(
              `Numeric parameter '${p.parameter_name || p.name}' (${tst.sample_type_name}) has no reference range.`
            );
          }
        }
      }
    }

    return issues;
  }, [drawerTestId, parameterMatchesTst, parametersByTest, rangesByParameter, testSampleTypesByTest, tests]);

  const startNew = () => {
    setSelectedTestId(null);
    setStepIndex(0);
    setPublishSuccess(false);
    setExpandedSampleTypeIds({});
    setEditingParamId(null);
    setParamEditDraftById({});
    setSelectedRangeSampleTypeId(null);
    setSelectedParameterId(null);
    setRangeEditId(null);
    setRangeEditDraftById({});
    setEditingQuestionId(null);
    setQuestionEditDraftById({});
    setSelectedTemplateParamIdsByTstId({});
    setCopyingTemplatesByTstId({});
    setDraft({
      id: null,
      category_id: "",
      name: "",
      code: "",
      description: "",
      price: "",
      is_active: 0,
      tube_type_id: "",
      processing_time: "",
      testing_mode: "analyzer",
      testing_modes: "",
      sample_volume: "",
      linear_range: "",
      sample_type_ids: [],
    });
    setSelectedParameterId(null);

    setDrawerMode("wizard");
    setDrawerTestId(null);
    setDrawerOpen(true);
  };

  const openExisting = async (testId, initialStep = 0) => {
    const test = tests.find((t) => t.id === testId);
    if (!test) return;

    setPublishSuccess(false);

    setDrawerMode("wizard");
    setDrawerTestId(testId);
    setDrawerOpen(true);

    setSelectedTestId(testId);
    const allowedSteps = stepsForTestingMode(test.testing_mode);
    setStepIndex(Math.max(0, Math.min(initialStep, allowedSteps.length - 1)));

    // sample_type_ids: try to infer from API-provided helpers if present
    const sampleTypeIds = Array.isArray(test.sample_type_ids)
      ? test.sample_type_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x))
      : [];

    setDraft({
      id: test.id,
      category_id: asString(test.category_id),
      name: asString(test.name),
      code: asString(test.code),
      description: asString(test.description),
      price: asString(test.price),
      is_active: test.is_active ? 1 : 0,
      tube_type_id: asString(test.tube_type_id),
      processing_time: asString(test.processing_time),
      testing_mode: normalizeTestingMode(test.testing_mode),
      testing_modes: asString(test.testing_modes),
      sample_volume: asString(test.sample_volume),
      linear_range: asString(test.linear_range),
      sample_type_ids: sampleTypeIds,
    });

    await dispatch(fetchTestSampleTypesByTest(test.id));
    await dispatch(fetchParametersByTest(test.id));
    await dispatch(fetchQuestionsByTest(test.id));

    // Reset UI-only state for the new context
    setExpandedSampleTypeIds({});
    setEditingParamId(null);
    setParamEditDraftById({});
    setSelectedRangeSampleTypeId(null);
    setSelectedParameterId(null);
    setRangeEditId(null);
    setRangeEditDraftById({});
    setEditingQuestionId(null);
    setQuestionEditDraftById({});
    setSelectedTemplateParamIdsByTstId({});
    setCopyingTemplatesByTstId({});
  };

  const openView = async (testId) => {
    setDrawerMode("view");
    setDrawerTestId(testId);
    setDrawerOpen(true);

    await unwrapOrToast(
      dispatch(fetchTestSampleTypesByTest(testId)),
      "Failed to load sample types"
    );
    const fetched = await unwrapOrToast(
      dispatch(fetchParametersByTest(testId)),
      "Failed to load parameters"
    );
    const ps = fetched?.parameters || [];

    // Prefetch ranges for all parameters so View can show full details.
    await Promise.all(
      (ps || []).map((p) => dispatch(fetchRangesByParameter(p.id)))
    );

    await unwrapOrToast(
      dispatch(fetchQuestionsByTest(testId)),
      "Failed to load questions"
    );
  };

  const deleteTestWithConfirm = async (testId) => {
    if (!testId) return;
    const t = tests.find((x) => x.id === testId);

    openConfirmDialog({
      title: "Delete test",
      message: `Delete test '${t?.name || testId}'? This cannot be undone.`,
      confirmText: "Delete",
      confirmTone: "danger",
      onConfirm: async () => {
        const action = await dispatch(deleteTest(testId));
        if (!deleteTest.fulfilled.match(action)) {
          // deleteTest thunk already toasts its own errors.
          return;
        }

        // Local UI cleanup if the deleted test was open.
        if (drawerTestId === testId) {
          setDrawerOpen(false);
          setDrawerMode("wizard");
          setDrawerTestId(null);
        }
        if (selectedTestId === testId) setSelectedTestId(null);
        if (draft.id === testId) {
          setStepIndex(0);
          setExpandedSampleTypeIds({});
          setEditingParamId(null);
          setParamEditDraftById({});
          setSelectedRangeSampleTypeId(null);
          setSelectedParameterId(null);
          setRangeEditId(null);
          setRangeEditDraftById({});
          setEditingQuestionId(null);
          setQuestionEditDraftById({});
          setSelectedTemplateParamIdsByTstId({});
          setCopyingTemplatesByTstId({});
          setPendingRemovedSampleTypeIds([]);
          setDraft((prev) => ({
            ...prev,
            id: null,
            category_id: "",
            name: "",
            code: "",
            description: "",
            price: "",
            is_active: 0,
            tube_type_id: "",
            processing_time: "",
            testing_modes: "",
            sample_volume: "",
            linear_range: "",
            sample_type_ids: [],
          }));
        }

        dispatch(fetchTests());
      },
    });
  };

  const deactivateTestWithConfirm = async (testId) => {
    if (!testId) return;
    const t = tests.find((x) => x.id === testId);
    openConfirmDialog({
      title: "Deactivate test",
      message: `Deactivate test '${t?.name || testId}'? This will make it not orderable.`,
      confirmText: "Deactivate",
      confirmTone: "danger",
      onConfirm: async () => {
        const ok = await unwrapOrToast(
          dispatch(updateTest({ id: testId, data: { is_active: 0 } })),
          "Failed to deactivate test"
        );
        if (!ok) return;
        dispatch(fetchTests());
      },
    });
  };

  // Keep join/parameters in sync if sample types are not available in the list response
  React.useEffect(() => {
    if (!draft.id) return;
    dispatch(fetchTestSampleTypesByTest(draft.id));
    dispatch(fetchParametersByTest(draft.id));
    dispatch(fetchQuestionsByTest(draft.id));
  }, [dispatch, draft.id]);

  // If the backend was down when the draft was selected, the initial fetch above can fail and
  // leave the wizard showing empty data. Re-fetch key data when entering steps that require it.
  React.useEffect(() => {
    if (!draft.id) return;
    const stepId = steps[stepIndex]?.id;
    if (stepId !== "parameters" && stepId !== "ranges" && stepId !== "confirm") return;
    dispatch(fetchTestSampleTypesByTest(draft.id));
    dispatch(fetchParametersByTest(draft.id));
  }, [dispatch, draft.id, stepIndex, steps]);

  // When editing an existing test, ensure the checkbox selection reflects the joined sample types.
  React.useEffect(() => {
    if (!draft.id) return;
    const rows = testSampleTypesByTest[draft.id];
    if (!Array.isArray(rows) || rows.length === 0) return;

    const nextIds = Array.from(
      new Set(
        rows
          .map((r) => Number(r.sample_type_id))
          .filter((x) => Number.isFinite(x))
      )
    );

    const currentIds = Array.from(
      new Set(
        (draft.sample_type_ids || [])
          .map((x) => Number(x))
          .filter((x) => Number.isFinite(x))
      )
    );

    const same =
      nextIds.length === currentIds.length &&
      nextIds.every((id) => currentIds.includes(id));

    if (!same) {
      setDraft((prev) => ({ ...prev, sample_type_ids: nextIds }));
    }
    // Intentionally not depending on draft.sample_type_ids to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id, testSampleTypesByTest]);

  const canGoToNext = () => {
    if (currentStep.id === "basics") {
      return Boolean(draft.category_id && draft.name && draft.code && draft.testing_mode);
    }
    if (currentStep.id === "parameters") {
      return Boolean(draft.id);
    }
    if (currentStep.id === "ranges") {
      return Boolean(draft.id);
    }
    if (currentStep.id === "questions") {
      return Boolean(draft.id);
    }
    return true;
  };

  const goNext = async () => {
    let ensuredTestId = draft.id;
    if (currentStep.id === "basics") {
      ensuredTestId = await saveBasicsAsDraft();
      if (!ensuredTestId) return;
    }

    const next = Math.min(stepIndex + 1, steps.length - 1);
    const nextStepId = steps[next]?.id;

    if (nextStepId === "ranges" && ensuredTestId) {
      // Prefetch ranges for all parameters (cheap enough, helps confirmation)
      const ps = parametersByTest[ensuredTestId] || [];
      for (const p of ps) {
        dispatch(fetchRangesByParameter(p.id));
      }
    }

    if (nextStepId === "confirm" && ensuredTestId) {
      await buildConfirmationPrefetch(ensuredTestId);
    }

    setStepIndex(next);
  };

  const goBack = () => {
    setStepIndex(Math.max(stepIndex - 1, 0));
  };

  // Ensure reference ranges are fetched by default whenever we are on the Ranges step.
  React.useEffect(() => {
    const isRanges = steps[stepIndex]?.id === "ranges";
    if (!isRanges) return;
    if (!draft.id) return;

    const ps = parametersByTest[draft.id] || [];
    for (const p of ps) {
      if (Object.prototype.hasOwnProperty.call(rangesByParameter, p.id)) continue;
      dispatch(fetchRangesByParameter(p.id));
    }
  }, [dispatch, stepIndex, draft.id, parametersByTest, rangesByParameter, steps]);

  const saveBasicsAsDraft = async () => {
    const normalizeIds = (xs) =>
      Array.from(
        new Set(
          (Array.isArray(xs) ? xs : [])
            .map((x) => Number(x))
            .filter((x) => Number.isFinite(x))
        )
      );

    const getEffectiveSampleTypeIds = () => {
      const chosen = normalizeIds(draft.sample_type_ids);
      if (chosen.length > 0) return chosen;
      if (!draft.id) return chosen;
      const rows = testSampleTypesByTest[draft.id] || [];
      const existing = normalizeIds(rows.map((r) => r.sample_type_id));
      return existing;
    };

    const effectiveSampleTypeIds = getEffectiveSampleTypeIds();

    // Frontend guardrails: backend requires at least one sample type.
    if (!draft.category_id || !draft.name) {
      toast.error("Category and Test Name are required");
      return null;
    }

    // If we didn't yet hydrate sample_type_ids from the DB joins, do it now to avoid accidental clearing.
    if (draft.id && normalizeIds(draft.sample_type_ids).length === 0 && effectiveSampleTypeIds.length > 0) {
      setDraft((prev) => ({ ...prev, sample_type_ids: effectiveSampleTypeIds }));
    }

    // If sample types were removed and dependencies exist, confirm & cascade-delete
    if (draft.id) {
      const currentTstRows = testSampleTypesByTest[draft.id] || [];
      const removed = currentTstRows.filter(
        (row) => !effectiveSampleTypeIds.includes(Number(row.sample_type_id))
      );

      if (removed.length > 0) {
        const currentParams = parametersByTest[draft.id] || [];
        const removedTstIds = removed.map((r) => r.id);
        const affectedParams = currentParams.filter((p) =>
          removedTstIds.includes(p.test_sample_type_id)
        );

        if (affectedParams.length > 0) {
          const ok = await confirmAsync({
            title: "Confirm removal",
            message:
              "Removing this sample type will remove its parameters and reference ranges. Continue?",
            confirmText: "Continue",
            confirmTone: "danger",
          });
          if (!ok) {
            // Re-add removed sample types to selection to match existing DB state
            setDraft((prev) => ({
              ...prev,
              sample_type_ids: Array.from(
                new Set([
                  ...prev.sample_type_ids,
                  ...removed.map((r) => r.sample_type_id),
                ])
              ),
            }));
            setPendingRemovedSampleTypeIds([]);
            return;
          }

          // Delete reference ranges -> parameters (best effort, in-order)
          for (const param of affectedParams) {
            let ranges = rangesByParameter[param.id];
            if (!Array.isArray(ranges)) {
              const fetched = await unwrapOrToast(
                dispatch(fetchRangesByParameter(param.id)),
                "Failed to fetch reference ranges"
              );
              ranges = fetched?.ranges;
            }
            if (Array.isArray(ranges)) {
              for (const r of ranges) {
                await unwrapOrToast(
                  dispatch(deleteReferenceRange(r.id)),
                  "Failed to delete reference range"
                );
              }
            }
            await unwrapOrToast(
              dispatch(deleteTestParameter(param.id)),
              "Failed to delete parameter"
            );
          }
          await dispatch(fetchParametersByTest(draft.id));
          setSelectedParameterId(null);
        }
      }
    }

    const payload = {
      category_id: toNumberOrNull(draft.category_id),
      name: draft.name,
      code: draft.code,
      description: draft.description || null,
      price: toNumberOrNull(draft.price),
      // keep draft inactive until confirmation
      is_active: 0,
      processing_time: draft.processing_time || null,
      testing_modes: draft.testing_modes || null,
      testing_mode: normalizeTestingMode(draft.testing_mode),
      sample_volume: draft.sample_volume || null,
      linear_range: draft.linear_range || null,
    };

    const hasSampleTypesLoaded =
      Boolean(draft.id) &&
      Object.prototype.hasOwnProperty.call(testSampleTypesByTest, draft.id);
    const chosenSampleTypeIds = normalizeIds(draft.sample_type_ids);

    // Only send join updates when we have an explicit selection OR we have loaded existing joins.
    // This avoids accidental clearing when editing and the joins haven't hydrated yet.
    if (!draft.id || chosenSampleTypeIds.length > 0 || hasSampleTypesLoaded) {
      // authoritative join update happens on backend
      payload.sample_type_ids = effectiveSampleTypeIds;
    }

    if (!draft.id) {
      const created = await unwrapOrToast(
        dispatch(createTest(payload)),
        "Failed to save test draft"
      );
      if (!created?.id) return;

      setDraft((prev) => ({ ...prev, id: created.id }));
      await dispatch(fetchTests());
      await dispatch(fetchTestSampleTypesByTest(created.id));
      await dispatch(fetchParametersByTest(created.id));
      await dispatch(fetchQuestionsByTest(created.id));
      setSelectedTestId(created.id);
      return created.id;
    }

    const ok = await unwrapOrToast(
      dispatch(updateTest({ id: draft.id, data: payload })),
      "Failed to update test draft"
    );
    if (!ok) return null;
    await dispatch(fetchTests());
    await dispatch(fetchTestSampleTypesByTest(draft.id));
    setPendingRemovedSampleTypeIds([]);
    return draft.id;
  };

  const onToggleSampleType = async (sampleTypeId) => {
    const id = Number(sampleTypeId);
    if (!Number.isFinite(id)) return;

    const current = Array.from(
      new Set(
        (Array.isArray(draft.sample_type_ids) ? draft.sample_type_ids : [])
          .map((x) => Number(x))
          .filter((x) => Number.isFinite(x))
      )
    );
    const exists = current.includes(id);

    // If removing a sample type that already exists on the test and has parameters/ranges, confirm.
    if (exists && draft.id) {
      const currentTstRows = testSampleTypesByTest[draft.id] || [];
      const row = currentTstRows.find((r) => r.sample_type_id === sampleTypeId);
      if (row) {
        const currentParams = parametersByTest[draft.id] || [];
        const affected = currentParams.filter((p) => String(p.test_sample_type_id) === String(row.id));
        if (affected.length > 0) {
          const ok = await confirmAsync({
            title: "Confirm removal",
            message:
              "Removing this sample type will remove its parameters and reference ranges when you save. Continue?",
            confirmText: "Continue",
            confirmTone: "danger",
          });
          if (!ok) return;
          setPendingRemovedSampleTypeIds((prev) =>
            prev.includes(sampleTypeId) ? prev : [...prev, sampleTypeId]
          );
        }
      }
    }

    const next = exists ? current.filter((x) => x !== id) : [...current, id];
    setDraft((prev) => ({ ...prev, sample_type_ids: next }));

    if (!exists) {
      // re-added; clear pending removal flag
      setPendingRemovedSampleTypeIds((prev) =>
        prev.filter((x) => x !== id)
      );
    }
  };

  const buildConfirmationPrefetch = async (testId) => {
    const id = testId || draft.id;
    if (!id) return;
    setConfirmPrefetching(true);
    try {
      const fetchedTsts = await unwrapOrToast(
        dispatch(fetchTestSampleTypesByTest(id)),
        "Failed to fetch test sample types"
      );

      const tsts = fetchedTsts?.sampleTypes || testSampleTypesByTest[id] || [];
      const uniqueSampleTypeIds = Array.from(
        new Set(
          (tsts || [])
            .map((row) => row.sample_type_id)
            .filter((x) => x !== null && x !== undefined)
        )
      );
      for (const stId of uniqueSampleTypeIds) {
        await unwrapOrToast(
          dispatch(fetchSampleTypeTubeTypes(stId)),
          "Failed to fetch sample type tube types"
        );
      }

      const fetchedParams = await unwrapOrToast(
        dispatch(fetchParametersByTest(id)),
        "Failed to fetch parameters"
      );

      const ps = fetchedParams?.parameters || [];
      for (const p of ps) {
        await unwrapOrToast(
          dispatch(fetchRangesByParameter(p.id)),
          "Failed to fetch reference ranges"
        );
      }

      await unwrapOrToast(
        dispatch(fetchQuestionsByTest(id)),
        "Failed to fetch questions"
      );
    } finally {
      setConfirmPrefetching(false);
    }
  };

  const confirmationIssues = React.useMemo(() => {
    const issues = [];
    if (!draft.id) {
      issues.push("Test is not saved as draft yet.");
      return issues;
    }

    const isMorphology = normalizeTestingMode(draft.testing_mode) === "morphology";

    const tsts = testSampleTypesByTest[draft.id] || [];
    if (tsts.length === 0) {
      issues.push("No sample types assigned.");
    }

    if (!isMorphology) {
      const ps = parametersByTest[draft.id] || [];
      for (const tst of tsts) {
        const forSampleType = ps.filter((p) => parameterMatchesTst(p, tst.id, draft.id));
        if (forSampleType.length === 0) {
          issues.push(`Sample type '${tst.sample_type_name}' has no parameters.`);
        }

        for (const p of forSampleType) {
          const isNumeric = (p.data_type || "numeric") === "numeric";
          if (!isNumeric) continue;
          const ranges = rangesByParameter[p.id] || [];
          if (ranges.length === 0) {
            issues.push(
              `Numeric parameter '${p.parameter_name || p.name}' (${tst.sample_type_name}) has no reference range.`
            );
          }
        }
      }
    }

    return issues;
  }, [draft.id, draft.testing_mode, parameterMatchesTst, parametersByTest, rangesByParameter, testSampleTypesByTest]);

  const confirmAndActivate = async () => {
    if (!draft.id) return;
    if (confirmationIssues.length > 0) return;

    const payload = {
      category_id: toNumberOrNull(draft.category_id),
      name: draft.name,
      code: draft.code,
      description: draft.description || null,
      price: toNumberOrNull(draft.price),
      is_active: 1,
      processing_time: draft.processing_time || null,
      testing_modes: draft.testing_modes || null,
      testing_mode: normalizeTestingMode(draft.testing_mode),
      sample_volume: draft.sample_volume || null,
      linear_range: draft.linear_range || null,
      sample_type_ids: draft.sample_type_ids.map((x) => Number(x)),
    };

    const updated = await unwrapOrToast(
      dispatch(updateTest({ id: draft.id, data: payload })),
      "Failed to activate test"
    );
    if (!updated) return;
    await dispatch(fetchTests());

    setDraft((prev) => ({ ...prev, is_active: 1 }));
    setPublishSuccess(true);
  };

  const parametersForSampleType = (tstId) =>
    parameters.filter((p) => parameterMatchesTst(p, tstId, draft.id));

  const getSampleTypeNameForTstId = (tstId) => {
    const row = (testSampleTypesByTest[draft.id] || []).find((r) => r.id === tstId);
    return row?.sample_type_name || "";
  };

  const ensureParameterDraft = (tstId) => {
    const existing = parameterDraftByTstId[tstId];
    if (existing) return existing;
    const next = {
      parameter_name: "",
      unit: "",
      data_type: "numeric",
    };
    setParameterDraftByTstId((prev) => ({ ...prev, [tstId]: next }));
    return next;
  };

  const setParameterDraft = (tstId, patch) => {
    setParameterDraftByTstId((prev) => ({
      ...prev,
      [tstId]: { ...(prev[tstId] || ensureParameterDraft(tstId)), ...patch },
    }));
  };

  const addParameter = async (tstId) => {
    if (!draft.id) return;
    const d = parameterDraftByTstId[tstId] || ensureParameterDraft(tstId);

    const name = String(d.parameter_name || "").trim();
    const unit = String(d.unit || "").trim();
    if (!name) {
      toast.error("Parameter name is required");
      return;
    }
    if (!unit) {
      toast.error("Unit is required");
      return;
    }

    const existing = parametersForSampleType(tstId);
    const nextOrder =
      existing.length > 0
        ? Math.max(...existing.map((p) => Number(p.display_order ?? 0))) + 1
        : 0;

    const created = await unwrapOrToast(
      dispatch(
        createTestParameter({
          test_id: draft.id,
          test_sample_type_id: tstId,
          parameter_name: name,
          unit,
          data_type: d.data_type,
          display_order: nextOrder,
        })
      ),
      "Failed to create parameter"
    );
    if (!created) return;

    setParameterDraftByTstId((prev) => ({
      ...prev,
      [tstId]: { parameter_name: "", unit: "", data_type: "numeric" },
    }));

    await dispatch(fetchParametersByTest(draft.id));
  };

  const toggleTemplateSelection = (tstId, templateParamId) => {
    setSelectedTemplateParamIdsByTstId((prev) => {
      const current = prev[tstId] || [];
      const exists = current.includes(templateParamId);
      const next = exists
        ? current.filter((id) => id !== templateParamId)
        : [...current, templateParamId];
      return { ...prev, [tstId]: next };
    });
  };

  const copySelectedTemplatesToTest = async (tst) => {
    if (!draft.id) return;
    if (!tst?.id) return;
    const sampleTypeId = tst.sample_type_id;
    if (!sampleTypeId) return;

    const selectedIds = selectedTemplateParamIdsByTstId[tst.id] || [];
    if (selectedIds.length === 0) return;

    setCopyingTemplatesByTstId((prev) => ({ ...prev, [tst.id]: true }));
    try {
      let templates = templateParametersBySampleTypeId[sampleTypeId];
      if (!Array.isArray(templates)) {
        const fetched = await unwrapOrToast(
          dispatch(fetchSampleTypeParameters(sampleTypeId)),
          "Failed to fetch sample type parameters"
        );
        templates = fetched?.items || [];
      }

      // Keep only active templates (is_active missing treated as active)
      const activeTemplates = (templates || []).filter(
        (tp) => tp?.is_active === undefined || tp?.is_active === 1 || tp?.is_active === true
      );

      const existingParams = parametersForSampleType(tst.id);
      const existingNames = new Set(
        existingParams
          .map((p) => String(p.parameter_name || p.name || "").trim().toLowerCase())
          .filter(Boolean)
      );

      const nextOrder =
        existingParams.length > 0
          ? Math.max(...existingParams.map((p) => Number(p.display_order ?? 0))) + 1
          : 0;
      let currentOrder = nextOrder;

      const missingUnitNames = [];
      const createdParamIds = [];

      for (const templateId of selectedIds) {
        const tp = activeTemplates.find((x) => x.id === templateId);
        if (!tp) continue;

        const name = String(tp.name || "").trim();
        const unit = tp.unit;
        const dataType = tp.data_type || "numeric";

        if (!name) continue;
        if (existingNames.has(name.toLowerCase())) continue;

        // Current DB schema requires unit for test parameters.
        if (!unit) {
          missingUnitNames.push(name);
          continue;
        }

        const createdParam = await unwrapOrToast(
          dispatch(
            createTestParameter({
              test_id: draft.id,
              test_sample_type_id: tst.id,
              parameter_name: name,
              unit,
              data_type: dataType,
              display_order: currentOrder,
            })
          ),
          `Failed to create parameter '${name}'`
        );
        if (!createdParam) continue;
        currentOrder += 1;

        if (createdParam?.id) {
          createdParamIds.push(createdParam.id);
        }

        let templateRanges = templateRangesByParameterId[templateId];
        if (!Array.isArray(templateRanges)) {
          const fetchedRanges = await unwrapOrToast(
            dispatch(fetchTemplateRanges(templateId)),
            "Failed to fetch template reference ranges"
          );
          templateRanges = fetchedRanges?.items || [];
        }

        if (Array.isArray(templateRanges) && templateRanges.length > 0 && createdParam?.id) {
          for (const r of templateRanges) {
            const isText = String(dataType || "numeric").toLowerCase() === "text";
            await unwrapOrToast(
              dispatch(
                createReferenceRange({
                  parameter_id: createdParam.id,
                  gender: normalizeGender(r.gender),
                  pregnancy_status: normalizePregnancy(r.pregnancy_status),
                  age_unit: r.age_unit || "years",
                  min_age: r.min_age ?? null,
                  max_age: r.max_age ?? null,
                  unit: r.unit ?? unit ?? null,
                  text_range: isText ? (r.text_range ?? null) : null,
                  remarks: r.remarks ?? null,
                  conditions: null,
                  min_value: isText ? null : r.min_value ?? null,
                  max_value: isText ? null : r.max_value ?? null,
                  critical_low: isText ? null : r.critical_low ?? null,
                  critical_high: isText ? null : r.critical_high ?? null,
                })
              ),
              "Failed to create reference range"
            );
          }
        }
      }

      if (missingUnitNames.length > 0) {
        toast.error(
          `Some templates were skipped because unit is required: ${missingUnitNames.join(", ")}. Fix units in Laboratory Configuration and try again.`
        );
      }

      await dispatch(fetchParametersByTest(draft.id));
      for (const pid of createdParamIds) {
        dispatch(fetchRangesByParameter(pid));
      }

      setSelectedTemplateParamIdsByTstId((prev) => ({ ...prev, [tst.id]: [] }));
    } finally {
      setCopyingTemplatesByTstId((prev) => ({ ...prev, [tst.id]: false }));
    }
  };

  const updateParam = async (param) => {
    const ok = await unwrapOrToast(
      dispatch(
        updateTestParameter({
          id: param.id,
          data: {
            parameter_name: param.parameter_name ?? param.name,
            unit: param.unit,
            data_type: param.data_type,
          },
        })
      ),
      "Failed to update parameter"
    );
    if (!ok) return;
    await dispatch(fetchParametersByTest(draft.id));
  };

  const removeParameter = async (parameterId) => {
    const ok = await unwrapOrToast(
      dispatch(deleteTestParameter(parameterId)),
      "Failed to delete parameter"
    );
    if (!ok) return;
    await dispatch(fetchParametersByTest(draft.id));
  };

  const moveParameter = async (tstId, parameterId, direction) => {
    const list = parametersForSampleType(tstId);
    const idx = list.findIndex((x) => x.id === parameterId);
    if (idx < 0) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const current = list[idx];
    const target = list[targetIdx];
    const currentOrder = Number(current.display_order ?? 0);
    const targetOrder = Number(target.display_order ?? 0);

    await Promise.all([
      unwrapOrToast(
        dispatch(
          updateTestParameter({
            id: current.id,
            data: { display_order: targetOrder },
          })
        ),
        "Failed to reorder parameter"
      ),
      unwrapOrToast(
        dispatch(
          updateTestParameter({
            id: target.id,
            data: { display_order: currentOrder },
          })
        ),
        "Failed to reorder parameter"
      ),
    ]);

    await dispatch(fetchParametersByTest(draft.id));
  };

  const removeParameterWithConfirm = async (parameterId) => {
    const ok = await confirmAsync({
      title: "Delete parameter",
      message: "Delete this parameter? This cannot be undone.",
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await removeParameter(parameterId);
  };

  const selectParameterForRanges = async (paramId) => {
    const pid = Number.isFinite(Number(paramId)) ? Number(paramId) : paramId;
    const p = (parametersByTest[draft.id] || []).find((x) => String(x.id) === String(pid));
    if (p?.test_sample_type_id) setSelectedRangeSampleTypeId(p.test_sample_type_id);
    setSelectedParameterId(pid);
    setRangeDraft((prev) => ({ ...prev, parameter_id: pid }));
    await dispatch(fetchRangesByParameter(pid));
  };

  const validateRangeDraftForSubmit = ({ parameter, draft: d }) => {
    if (!parameter?.id) {
      toast.error("Select a parameter first");
      return false;
    }

    const dataType = String(parameter.data_type || "numeric").toLowerCase();
    const isText = dataType === "text";
    if (dataType !== "numeric" && !isText) {
      toast.error("Unsupported parameter type for reference ranges");
      return false;
    }

    const hasText = (v) => String(v ?? "").trim().length > 0;

    const minAge = hasText(d.min_age) ? Number(d.min_age) : null;
    const maxAge = hasText(d.max_age) ? Number(d.max_age) : null;
    if (minAge !== null && Number.isNaN(minAge)) {
      toast.error("Min age must be a number");
      return false;
    }
    if (maxAge !== null && Number.isNaN(maxAge)) {
      toast.error("Max age must be a number");
      return false;
    }
    if (minAge !== null && maxAge !== null && minAge > maxAge) {
      toast.error("Min age cannot be greater than max age");
      return false;
    }

    if (isText) {
      if (!hasText(d.text_range)) {
        toast.error("Range is required for text parameters");
        return false;
      }
      return true;
    }

    const minValue = hasText(d.min_value) ? Number(d.min_value) : null;
    const maxValue = hasText(d.max_value) ? Number(d.max_value) : null;
    if (minValue !== null && Number.isNaN(minValue)) {
      toast.error("Min value must be a number");
      return false;
    }
    if (maxValue !== null && Number.isNaN(maxValue)) {
      toast.error("Max value must be a number");
      return false;
    }

    // Practical requirement: a range without bounds is not useful.
    if (minValue === null && maxValue === null) {
      toast.error("Enter at least Min value or Max value");
      return false;
    }

    if (minValue !== null && maxValue !== null && minValue > maxValue) {
      toast.error("Min value cannot be greater than max value");
      return false;
    }

    const criticalLow = hasText(d.critical_low) ? Number(d.critical_low) : null;
    const criticalHigh = hasText(d.critical_high) ? Number(d.critical_high) : null;
    if (criticalLow !== null && Number.isNaN(criticalLow)) {
      toast.error("Critical low must be a number");
      return false;
    }
    if (criticalHigh !== null && Number.isNaN(criticalHigh)) {
      toast.error("Critical high must be a number");
      return false;
    }
    if (criticalLow !== null && criticalHigh !== null && criticalLow > criticalHigh) {
      toast.error("Critical low cannot be greater than critical high");
      return false;
    }

    return true;
  };

  const addRange = async () => {
    if (!selectedParameterId) {
      toast.error("Select a parameter first");
      return;
    }

    const param = (parametersByTest[draft.id] || []).find(
      (x) => String(x.id) === String(selectedParameterId)
    );
    if (!validateRangeDraftForSubmit({ parameter: param, draft: rangeDraft })) return;

    const parsedFactors = parseFactorsLines(rangeDraft.factors);
    if (parsedFactors === undefined) {
      // eslint-disable-next-line no-alert
      toast.error('Factors must be one per line as "key=value" (or left empty).');
      return;
    }

    const payload = (param?.data_type || "numeric") === "text"
      ? {
          gender: rangeDraft.gender,
          pregnancy_status: rangeDraft.pregnancy_status,
          age_unit: rangeDraft.age_unit || "years",
          min_age: toNumberOrNull(rangeDraft.min_age),
          max_age: toNumberOrNull(rangeDraft.max_age),
          text_range: rangeDraft.text_range || null,
          remarks: rangeDraft.remarks || null,
          conditions: null,
          min_value: null,
          max_value: null,
          critical_low: null,
          critical_high: null,
          factors: parsedFactors,
        }
      : {
          gender: rangeDraft.gender,
          pregnancy_status: rangeDraft.pregnancy_status,
          age_unit: rangeDraft.age_unit || "years",
          min_age: toNumberOrNull(rangeDraft.min_age),
          max_age: toNumberOrNull(rangeDraft.max_age),
          min_value: toNumberOrNull(rangeDraft.min_value),
          max_value: toNumberOrNull(rangeDraft.max_value),
          critical_low: toNumberOrNull(rangeDraft.critical_low),
          critical_high: toNumberOrNull(rangeDraft.critical_high),
          text_range: null,
          remarks: rangeDraft.remarks || null,
          conditions: null,
          factors: parsedFactors,
        };

    const ok = await unwrapOrToast(
      dispatch(
        createReferenceRange({
          parameter_id: selectedParameterId,
          data: payload,
        })
      ),
      "Failed to create reference range"
    );
    if (!ok) return;

    setRangeDraft({
      parameter_id: selectedParameterId,
      gender: "both",
      pregnancy_status: "both",
      age_unit: "years",
      min_age: "",
      max_age: "",
      min_value: "",
      max_value: "",
      critical_low: "",
      critical_high: "",
      text_range: "",
      remarks: "",
      factors: "",
    });

    await dispatch(fetchRangesByParameter(selectedParameterId));
  };

  const onUpdateRangeInline = async (range, patch) => {
    const ok = await unwrapOrToast(
      dispatch(updateReferenceRange({ id: range.id, data: { ...range, ...patch } })),
      "Failed to update reference range"
    );
    if (!ok) return;
    await dispatch(fetchRangesByParameter(range.parameter_id));
  };

  const removeRange = async (rangeId, parameterId) => {
    const ok = await unwrapOrToast(
      dispatch(deleteReferenceRange(rangeId)),
      "Failed to delete reference range"
    );
    if (!ok) return;
    await dispatch(fetchRangesByParameter(parameterId));
  };

  const removeRangeWithConfirm = async (rangeId, parameterId) => {
    const ok = await confirmAsync({
      title: "Delete reference range",
      message: "Delete this reference range? This cannot be undone.",
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await removeRange(rangeId, parameterId);
  };

  const addQuestion = async () => {
    if (!draft.id) return;
    const dbFieldType = mapUiFieldTypeToDb(questionDraft.field_type);

    const optionsArray = parseOptionsLines(questionDraft.options);
    if (needsOptionsForDbFieldType(dbFieldType)) {
      if (optionsArray.length === 0) {
        // eslint-disable-next-line no-alert
        toast.error("Options are required (enter one option per line).");
        return;
      }
    }

    const ok = await unwrapOrToast(
      dispatch(
        createDynamicQuestion({
          test_id: draft.id,
          question_text: questionDraft.question_text,
          field_type: dbFieldType,
          options: needsOptionsForDbFieldType(dbFieldType) ? optionsArray : null,
          is_required: questionDraft.is_required ? 1 : 0,
        })
      ),
      "Failed to create question"
    );
    if (!ok) return;

    setQuestionDraft({
      question_text: "",
      field_type: "text",
      options: "",
      is_required: 0,
    });

    await dispatch(fetchQuestionsByTest(draft.id));
  };

  const removeQuestion = async (id) => {
    const ok = await unwrapOrToast(
      dispatch(deleteDynamicQuestion(id)),
      "Failed to delete question"
    );
    if (!ok) return;
    await dispatch(fetchQuestionsByTest(draft.id));
  };

  const removeQuestionWithConfirm = async (id) => {
    const ok = await confirmAsync({
      title: "Delete question",
      message: "Delete this question? This cannot be undone.",
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await removeQuestion(id);
  };

  const updateQuestionInline = async (q, patch) => {
    const next = { ...q, ...patch };
    if (patch.field_type !== undefined) {
      next.field_type = mapUiFieldTypeToDb(patch.field_type);
      if (next.field_type === "text") {
        next.options = [];
      }
    }
    const ok = await unwrapOrToast(
      dispatch(updateDynamicQuestion({ id: q.id, data: next })),
      "Failed to update question"
    );
    if (!ok) return;
    await dispatch(fetchQuestionsByTest(draft.id));
  };

  const canGoToIndex = (idx) => idx <= stepIndex;

  const stepSummary = React.useMemo(() => {
    const tsts = draft.id ? testSampleTypesByTest[draft.id] || [] : [];
    const ps = draft.id ? parametersByTest[draft.id] || [] : [];
    const q = draft.id ? questionsByTest[draft.id] || [] : [];

    const isMorphology = normalizeTestingMode(draft.testing_mode) === "morphology";
    const basicsOk = Boolean(draft.category_id && draft.name && draft.code && draft.testing_mode);
    const sampleTypesOk = draft.id ? tsts.length > 0 : false;

    const hasAnyParams = ps.length > 0;
    const hasAnyRanges = ps.some((p) => (rangesByParameter[p.id] || []).length > 0);

    return {
      basics: {
        tone: basicsOk ? "success" : "warning",
        badge: basicsOk ? "Ready" : "Required",
        hint: basicsOk ? "Details captured" : "Fill required fields",
      },
      parameters: {
        tone: isMorphology ? "neutral" : sampleTypesOk && hasAnyParams ? "success" : "warning",
        badge: isMorphology ? "Skipped" : hasAnyParams ? `${ps.length} added` : "Required",
        hint: isMorphology
          ? "Not required for morphology"
          : sampleTypesOk
          ? "Add parameters per sample type"
          : "Assign sample types first",
      },
      ranges: {
        tone: isMorphology ? "neutral" : hasAnyRanges ? "success" : "neutral",
        badge: isMorphology ? "Skipped" : hasAnyRanges ? "Started" : "Optional",
        hint: isMorphology ? "Not required for morphology" : "Define ranges for numeric parameters",
      },
      questions: {
        tone: q.length > 0 ? "info" : "neutral",
        badge: q.length > 0 ? `${q.length} added` : "Optional",
        hint: "Add ordering questions (optional)",
      },
      confirm: {
        tone: confirmationIssues.length === 0 ? "success" : "danger",
        badge: confirmationIssues.length === 0 ? "Pass" : "Review",
        hint:
          confirmationIssues.length === 0
            ? "Ready to activate"
            : "Resolve missing configuration",
      },
    };
  }, [
    confirmationIssues.length,
    draft.category_id,
    draft.code,
    draft.id,
    draft.name,
    draft.testing_mode,
    parametersByTest,
    questionsByTest,
    rangesByParameter,
    testSampleTypesByTest,
  ]);

  const renderBasics = () => {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white ring-1 ring-indigo-100">
              <TestTube className="h-5 w-5 text-indigo-700" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-900">Start with the basics</div>
              <div className="mt-0.5 text-xs text-slate-600">
                Define the test, then assign sample types. Parameters and ranges come next.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="Test details"
              subtitle="Fields marked * are required"
              right={
                <Button
                  type="button"
                  tone="soft"
                  size="sm"
                  onClick={saveBasicsAsDraft}
                  disabled={!draft.category_id || !draft.name || !draft.code}
                >
                  <Save className="h-4 w-4" />
                  Save draft
                </Button>
              }
            />
            <CardBody>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Category *">
                  <Select
                    value={draft.category_id}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, category_id: e.target.value }))
                    }
                  >
                    <option value="">Select category</option>
                    {testCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="Test name *">
                  <TextInput
                    value={draft.name}
                    placeholder="e.g. Complete Blood Count"
                    onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                  />
                </Field>

                <Field label="Code *" hint="Short identifier">
                  <TextInput
                    value={draft.code}
                    placeholder="e.g. CBC"
                    onChange={(e) => setDraft((p) => ({ ...p, code: e.target.value }))}
                  />
                </Field>

                <Field label="Price" hint="Optional">
                  <TextInput
                    value={draft.price}
                    inputMode="decimal"
                    placeholder="0.00"
                    onChange={(e) => setDraft((p) => ({ ...p, price: e.target.value }))}
                  />
                </Field>

                <Field label="Processing time" hint="Optional">
                  <TextInput
                    value={draft.processing_time}
                    placeholder="e.g. 2 hours"
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, processing_time: e.target.value }))
                    }
                  />
                </Field>

                <Field
                  label="Testing mode *"
                  hint="Analyzer-based tests use parameters/ranges; Morphology uses questions + pathologist report"
                >
                  <Select
                    value={draft.testing_mode}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, testing_mode: e.target.value }))
                    }
                  >
                    {TESTING_MODE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="Analyzer modes" hint="Optional (legacy)">
                  <TextInput
                    value={draft.testing_modes}
                    placeholder="e.g. Routine / STAT"
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, testing_modes: e.target.value }))
                    }
                  />
                </Field>

                <Field label="Sample volume" hint="Optional">
                  <TextInput
                    value={draft.sample_volume}
                    placeholder="e.g. 2 mL"
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, sample_volume: e.target.value }))
                    }
                  />
                </Field>

                <Field label="Linear range" hint="Optional">
                  <TextInput
                    value={draft.linear_range}
                    placeholder="e.g. 0–200"
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, linear_range: e.target.value }))
                    }
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field label="Description" hint="Optional">
                    <TextArea
                      value={draft.description}
                      rows={3}
                      placeholder="Short clinical description or notes"
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, description: e.target.value }))
                      }
                    />
                  </Field>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Sample types"
              subtitle="Pick all sample types this test can be performed on"
            />
            <CardBody>
              {pendingRemovedSampleTypeIds.length > 0 ? (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4" />
                    <div>
                      <div className="font-medium">Pending removal</div>
                      <div className="text-xs text-amber-800 mt-0.5">
                        You removed sample types that already had parameters. When you save draft, their parameters and ranges will be removed.
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(sampleTypes || []).map((st) => {
                  const checked = draft.sample_type_ids.includes(st.id);
                  return (
                    <label
                      key={st.id}
                      className={cx(
                        "flex cursor-pointer items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition",
                        checked
                          ? "border-indigo-200 bg-indigo-50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          checked={checked}
                          onChange={() => onToggleSampleType(st.id)}
                        />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{st.name}</div>
                          <div className="text-xs text-slate-500">sample_type_id: {st.id}</div>
                        </div>
                      </div>
                      {checked ? <Badge tone="info">Selected</Badge> : null}
                    </label>
                  );
                })}
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <div className="flex items-start gap-2">
                  <HelpCircle className="mt-0.5 h-4 w-4 text-slate-500" />
                  <div>
                    Save Draft creates/updates the test and assigns sample types. Next, define parameters per sample type.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  };

  const renderParameters = () => {
    if (!draft.id) {
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>
              <div className="font-semibold">Save the draft first</div>
              <div className="mt-1 text-xs text-amber-800">
                Parameters are stored per sample type. Save the test draft so the system can create the sample-type join records.
              </div>
            </div>
          </div>
        </div>
      );
    }

    const toggleExpanded = (tstId) =>
      setExpandedSampleTypeIds((prev) => {
        const isOpen = prev[tstId] === true;
        return { ...prev, [tstId]: !isOpen };
      });

    const beginEditParam = (p) => {
      setEditingParamId(p.id);
      setParamEditDraftById((prev) => ({
        ...prev,
        [p.id]: {
          parameter_name: asString(p.parameter_name ?? p.name),
          unit: asString(p.unit),
          data_type: asString(p.data_type || "numeric"),
        },
      }));
    };

    const cancelEditParam = () => setEditingParamId(null);

    const saveEditParam = async (p) => {
      const d = paramEditDraftById[p.id];
      if (!d) return;
      await updateParam({
        ...p,
        parameter_name: d.parameter_name,
        unit: d.unit,
        data_type: d.data_type,
      });
      setEditingParamId(null);
    };

    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white ring-1 ring-slate-200">
              <SlidersHorizontal className="h-5 w-5 text-slate-700" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-900">Parameters per sample type</div>
              <div className="mt-0.5 text-xs text-slate-600">
                Add the parameters that will be measured for each sample type.
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {testSampleTypes.map((tst) => {
            const pd = parameterDraftByTstId[tst.id] || ensureParameterDraft(tst.id);
            const ps = parametersForSampleType(tst.id);
            const expanded = expandedSampleTypeIds[tst.id] === true;

            const sampleTypeId = tst.sample_type_id;
            const templatesLoaded = Object.prototype.hasOwnProperty.call(
              templateParametersBySampleTypeId,
              sampleTypeId
            );
            const templateParamsRaw = templatesLoaded
              ? templateParametersBySampleTypeId[sampleTypeId] || []
              : [];
            const templateParams = (templateParamsRaw || []).filter(
              (tp) => tp?.is_active === undefined || tp?.is_active === 1 || tp?.is_active === true
            );
            const selectedTemplateIds = selectedTemplateParamIdsByTstId[tst.id] || [];
            const copying = copyingTemplatesByTstId[tst.id] === true;
            const existingNameSet = new Set(
              ps
                .map((p) => String(p.parameter_name || p.name || "").trim().toLowerCase())
                .filter(Boolean)
            );

            return (
              <Card key={tst.id}>
                <button
                  type="button"
                  className="w-full px-5 py-4 text-left hover:bg-slate-50"
                  onClick={() => toggleExpanded(tst.id)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {tst.sample_type_name}
                        </span>
                        <Badge tone="neutral">{ps.length} parameter(s)</Badge>
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        test_sample_types.id: {tst.id}
                      </div>
                    </div>
                    {expanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-500" />
                    )}
                  </div>
                </button>

                {expanded ? (
                  <CardBody className="border-t border-slate-100">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">
                            Templates (from Sample Type)
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            Select predefined parameters for this sample type and copy them into this test.
                          </div>
                        </div>
                        <Button
                          type="button"
                          tone="soft"
                          size="sm"
                          disabled={copying || selectedTemplateIds.length === 0}
                          onClick={() => copySelectedTemplatesToTest(tst)}
                        >
                          {copying ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ClipboardCheck className="h-4 w-4" />
                          )}
                          Copy selected ({selectedTemplateIds.length})
                        </Button>
                      </div>

                      <div className="mt-3">
                        {!sampleTypeId ? (
                          <div className="text-sm text-slate-600">
                            No sample_type_id found for this join row.
                          </div>
                        ) : !templatesLoaded ? (
                          <div className="text-sm text-slate-600">Loading templates…</div>
                        ) : templateParams.length === 0 ? (
                          <div className="text-sm text-slate-600">
                            No active templates defined for this sample type.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-2">
                            {templateParams.map((tp) => {
                              const name = String(tp.name || "").trim();
                              const alreadyAdded = existingNameSet.has(name.toLowerCase());
                              const checked = selectedTemplateIds.includes(tp.id);
                              return (
                                <label
                                  key={tp.id}
                                  className={cx(
                                    "flex cursor-pointer items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3",
                                    checked
                                      ? "border-indigo-200 bg-indigo-50"
                                      : "border-slate-200 hover:bg-slate-50",
                                    alreadyAdded ? "opacity-60" : ""
                                  )}
                                >
                                  <div className="flex items-start gap-3">
                                    <input
                                      type="checkbox"
                                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                      disabled={alreadyAdded}
                                      checked={checked}
                                      onChange={() => toggleTemplateSelection(tst.id, tp.id)}
                                    />
                                    <div className="min-w-0">
                                      <div className="text-sm font-semibold text-slate-900">
                                        <span className="truncate">{name || "(Unnamed)"}</span>
                                      </div>
                                      <div className="mt-0.5 text-xs text-slate-600">
                                        {(tp.data_type || "numeric") +
                                          " • Unit: " +
                                          (tp.unit || "-")}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge
                                      tone={(tp.data_type || "numeric") === "numeric" ? "info" : "neutral"}
                                    >
                                      {tp.data_type || "numeric"}
                                    </Badge>
                                    {alreadyAdded ? <Badge tone="warning">Already added</Badge> : null}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <Field label="Parameter name">
                        <TextInput
                          placeholder="e.g. Hemoglobin"
                          value={pd.parameter_name}
                          onChange={(e) =>
                            setParameterDraft(tst.id, { parameter_name: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Unit" hint="Required">
                        <TextInput
                          placeholder="e.g. g/dL"
                          value={pd.unit}
                          onChange={(e) => setParameterDraft(tst.id, { unit: e.target.value })}
                        />
                      </Field>
                      <Field label="Data type">
                        <Select
                          value={pd.data_type}
                          onChange={(e) => setParameterDraft(tst.id, { data_type: e.target.value })}
                        >
                          {DATA_TYPES.map((dt) => (
                            <option key={dt.value} value={dt.value}>
                              {dt.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          tone="secondary"
                          className="w-full"
                          onClick={() => addParameter(tst.id)}
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4">
                      {ps.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                          No parameters yet — add one above.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {ps.map((p) => {
                            const isEditing = editingParamId === p.id;
                            const d =
                              paramEditDraftById[p.id] ||
                              {
                                parameter_name: asString(p.parameter_name ?? p.name),
                                unit: asString(p.unit),
                                data_type: asString(p.data_type || "numeric"),
                              };
                            const dt = d.data_type || "numeric";

                            return (
                              <div
                                key={p.id}
                                className="rounded-2xl border border-slate-200 bg-white p-4"
                              >
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <div className="text-sm font-semibold text-slate-900">
                                        {isEditing ? (
                                          <TextInput
                                            value={d.parameter_name}
                                            onChange={(e) =>
                                              setParamEditDraftById((prev) => ({
                                                ...prev,
                                                [p.id]: { ...d, parameter_name: e.target.value },
                                              }))
                                            }
                                          />
                                        ) : (
                                          <span className="truncate">{p.parameter_name || p.name}</span>
                                        )}
                                      </div>
                                      <Badge tone={dt === "numeric" ? "info" : "neutral"}>{dt}</Badge>
                                      {(p.data_type || "numeric") !== "numeric" ? (
                                        <Badge tone="neutral">Ranges not required</Badge>
                                      ) : null}
                                    </div>

                                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                                      <Field label="Unit" hint={dt === "numeric" ? "Required (shown on reports)" : "Required"}>
                                        {isEditing ? (
                                          <TextInput
                                            value={d.unit}
                                            onChange={(e) =>
                                              setParamEditDraftById((prev) => ({
                                                ...prev,
                                                [p.id]: { ...d, unit: e.target.value },
                                              }))
                                            }
                                          />
                                        ) : (
                                          <div className="text-sm text-slate-700">{p.unit || "-"}</div>
                                        )}
                                      </Field>
                                      <Field label="Data type">
                                        {isEditing ? (
                                          <Select
                                            value={d.data_type}
                                            onChange={(e) =>
                                              setParamEditDraftById((prev) => ({
                                                ...prev,
                                                [p.id]: { ...d, data_type: e.target.value },
                                              }))
                                            }
                                          >
                                            {DATA_TYPES.map((x) => (
                                              <option key={x.value} value={x.value}>
                                                {x.label}
                                              </option>
                                            ))}
                                          </Select>
                                        ) : (
                                          <div className="text-sm text-slate-700">{p.data_type || "numeric"}</div>
                                        )}
                                      </Field>
                                      <div className="hidden md:block" />
                                    </div>
                                  </div>

                                  <div className="flex shrink-0 items-center gap-2">
                                    {isEditing ? (
                                      <>
                                        <Button
                                          type="button"
                                          tone="primary"
                                          size="sm"
                                          onClick={() => saveEditParam(p)}
                                        >
                                          <Save className="h-4 w-4" />
                                          Save
                                        </Button>
                                        <Button
                                          type="button"
                                          tone="soft"
                                          size="sm"
                                          onClick={cancelEditParam}
                                        >
                                          <X className="h-4 w-4" />
                                          Cancel
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button
                                          type="button"
                                          tone="soft"
                                          size="sm"
                                          onClick={() => moveParameter(tst.id, p.id, -1)}
                                          disabled={ps.findIndex((x) => x.id === p.id) === 0}
                                        >
                                          <ArrowUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          type="button"
                                          tone="soft"
                                          size="sm"
                                          onClick={() => moveParameter(tst.id, p.id, 1)}
                                          disabled={ps.findIndex((x) => x.id === p.id) === ps.length - 1}
                                        >
                                          <ArrowDown className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          type="button"
                                          tone="soft"
                                          size="sm"
                                          onClick={() => beginEditParam(p)}
                                        >
                                          <Pencil className="h-4 w-4" />
                                          Edit
                                        </Button>
                                        <Button
                                          type="button"
                                          tone="soft"
                                          size="sm"
                                          onClick={() => {
                                            selectParameterForRanges(p.id);
                                            setStepIndex(2);
                                          }}
                                        >
                                          <FlaskConical className="h-4 w-4" />
                                          Ranges
                                        </Button>
                                        <Button
                                          type="button"
                                          tone="danger"
                                          size="sm"
                                          onClick={() => removeParameterWithConfirm(p.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Delete
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardBody>
                ) : null}
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRanges = () => {
    if (!draft.id) {
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>
              <div className="font-semibold">Save the draft first</div>
              <div className="mt-1 text-xs text-amber-800">
                Reference ranges are attached to parameters. Save the draft, then define parameters.
              </div>
            </div>
          </div>
        </div>
      );
    }

    const tsts = testSampleTypesByTest[draft.id] || [];
    const activeTstId = selectedRangeSampleTypeId || tsts[0]?.id || null;
    const filteredParams = activeTstId
      ? (parametersByTest[draft.id] || []).filter(
          (p) => parameterMatchesTst(p, activeTstId, draft.id)
        )
      : [];

    const selectedParam = parameters.find((p) => String(p.id) === String(selectedParameterId));
    const selectedParamSampleTypeName = selectedParam?.test_sample_type_id
      ? getSampleTypeNameForTstId(selectedParam.test_sample_type_id)
      : "";
    const ranges = selectedParameterId ? rangesByParameter[selectedParameterId] || [] : [];
    const isNumeric = (selectedParam?.data_type || "numeric") === "numeric";
    const isTextParam = String(selectedParam?.data_type || "numeric").toLowerCase() === "text";
    const coverageWarnings = selectedParameterId ? computeCoverageWarnings(ranges) : [];

    const beginEditRange = (r) => {
      setRangeEditId(r.id);
      setRangeEditDraftById((prev) => ({
        ...prev,
        [r.id]: {
          gender: normalizeGender(r.gender),
          pregnancy_status: normalizePregnancy(r.pregnancy_status),
          age_unit: r.age_unit || "years",
          min_age: asString(r.min_age),
          max_age: asString(r.max_age),
          min_value: asString(r.min_value),
          max_value: asString(r.max_value),
          critical_low: asString(r.critical_low),
          critical_high: asString(r.critical_high),
          text_range: asString(r.text_range),
          remarks: asString(r.remarks),
          factors: formatFactorsLines(r.factors),
        },
      }));
    };

    const cancelEditRange = () => setRangeEditId(null);

    const saveEditRange = async (r) => {
      const d = rangeEditDraftById[r.id];
      if (!d) return;

      const param = (parametersByTest[draft.id] || []).find(
        (x) => String(x.id) === String(r.parameter_id)
      );
      if (!validateRangeDraftForSubmit({ parameter: param, draft: d })) return;

      const parsedFactors = parseFactorsLines(d.factors);
      if (parsedFactors === undefined) {
        // eslint-disable-next-line no-alert
        toast.error('Factors must be one per line as "key=value" (or left empty).');
        return;
      }
      const payload = (param?.data_type || "numeric") === "text"
        ? {
            gender: normalizeGender(d.gender),
            pregnancy_status: normalizePregnancy(d.pregnancy_status),
            age_unit: d.age_unit || "years",
            min_age: toNumberOrNull(d.min_age),
            max_age: toNumberOrNull(d.max_age),
            unit: param?.unit || r.unit || null,
            text_range: d.text_range || null,
            remarks: d.remarks || null,
            conditions: null,
            min_value: null,
            max_value: null,
            critical_low: null,
            critical_high: null,
            factors: parsedFactors,
          }
        : {
            gender: normalizeGender(d.gender),
            pregnancy_status: normalizePregnancy(d.pregnancy_status),
            age_unit: d.age_unit || "years",
            min_age: toNumberOrNull(d.min_age),
            max_age: toNumberOrNull(d.max_age),
            unit: param?.unit || r.unit || null,
            min_value: toNumberOrNull(d.min_value),
            max_value: toNumberOrNull(d.max_value),
            critical_low: toNumberOrNull(d.critical_low),
            critical_high: toNumberOrNull(d.critical_high),
            text_range: null,
            remarks: d.remarks || null,
            conditions: null,
            factors: parsedFactors,
          };
      await onUpdateRangeInline(r, payload);
      setRangeEditId(null);
    };

    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white ring-1 ring-slate-200">
              <FlaskConical className="h-5 w-5 text-slate-700" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-900">Ranges belong to parameters</div>
              <div className="mt-0.5 text-xs text-slate-600">
                Choose a sample type, then choose a parameter to add ranges.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader
                title="Context"
                subtitle="Choose sample type and parameter"
              />
              <CardBody className="space-y-4">
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-2">Sample type</div>
                  <div className="space-y-2">
                    {tsts.length === 0 ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                        No sample types assigned.
                      </div>
                    ) : (
                      tsts.map((tst) => {
                        const active = String(activeTstId) === String(tst.id);
                        const count = (parametersByTest[draft.id] || []).filter(
                          (p) => parameterMatchesTst(p, tst.id, draft.id)
                        ).length;
                        return (
                          <button
                            key={tst.id}
                            type="button"
                            onClick={() => {
                              setSelectedRangeSampleTypeId(tst.id);
                              setSelectedParameterId(null);
                              setRangeEditId(null);
                            }}
                            className={cx(
                              "w-full rounded-2xl border px-4 py-3 text-left transition",
                              active
                                ? "border-indigo-200 bg-indigo-50"
                                : "border-slate-200 bg-white hover:bg-slate-50"
                            )}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-slate-900">{tst.sample_type_name}</div>
                                <div className="mt-0.5 text-xs text-slate-500">{count} parameter(s)</div>
                              </div>
                              {active ? <Badge tone="info">Selected</Badge> : null}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-slate-700 mb-2">Parameters</div>
                  {!activeTstId ? (
                    <div className="text-sm text-slate-500">Select a sample type above.</div>
                  ) : filteredParams.length === 0 ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                      No parameters for this sample type yet.
                      <div className="mt-2">
                        <Button
                          type="button"
                          tone="soft"
                          size="sm"
                          onClick={() => setStepIndex(1)}
                        >
                          Go to Parameters
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredParams.map((p) => {
                        const active = selectedParameterId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => selectParameterForRanges(p.id)}
                            className={cx(
                              "w-full rounded-2xl border px-4 py-3 text-left transition",
                              active
                                ? "border-indigo-200 bg-indigo-50"
                                : "border-slate-200 bg-white hover:bg-slate-50"
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-slate-900">
                                  {p.parameter_name || p.name}
                                </div>
                                <div className="mt-0.5 text-xs text-slate-500">
                                  {p.data_type || "numeric"} • Unit: {p.unit || "-"}
                                </div>
                              </div>
                              <Badge tone={(p.data_type || "numeric") === "numeric" ? "info" : "neutral"}>
                                {p.data_type || "numeric"}
                              </Badge>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card>
              <CardHeader
                title={
                  selectedParam
                    ? "Range editor"
                    : "Select a parameter to manage ranges"
                }
                subtitle={
                  selectedParam
                    ? `${selectedParamSampleTypeName} → ${selectedParam.parameter_name || selectedParam.name}`
                    : "Ranges apply to a specific parameter"
                }
                right={
                  selectedParam ? (
                    <Badge tone={isNumeric ? "info" : "neutral"}>
                      {selectedParam.data_type || "numeric"}
                    </Badge>
                  ) : null
                }
              />
              <CardBody>
                {!selectedParam ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    Choose a parameter on the left to view and add reference ranges.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {isTextParam ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                        This parameter is <span className="font-semibold">{selectedParam.data_type}</span>. Use text ranges instead of numeric min/max values.
                      </div>
                    ) : null}

                    {coverageWarnings.length > 0 ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-900" />
                          <div>
                            <div className="text-sm font-semibold text-amber-950">
                              Coverage warnings (basic)
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-900">
                              {coverageWarnings.map((w, idx) => (
                                <li key={idx}>{w}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-900">Add a range</div>
                        <Badge tone="neutral">parameter_id: {selectedParam.id}</Badge>
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Field label="Gender" hint="Optional">
                          <Select
                            value={rangeDraft.gender}
                            onChange={(e) => setRangeDraft((p) => ({ ...p, gender: e.target.value }))}
                          >
                            <option value="both">Any</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </Select>
                        </Field>
                        <Field label="Pregnancy status" hint="Optional">
                          <Select
                            value={rangeDraft.pregnancy_status}
                            onChange={(e) => setRangeDraft((p) => ({ ...p, pregnancy_status: e.target.value }))}
                          >
                            <option value="both">Any</option>
                            <option value="pregnant">Pregnant</option>
                            <option value="not_pregnant">Not pregnant</option>
                          </Select>
                        </Field>

                        <Field label="Age unit">
                          <Select
                            value={rangeDraft.age_unit}
                            onChange={(e) => setRangeDraft((p) => ({ ...p, age_unit: e.target.value }))}
                          >
                            <option value="years">Years</option>
                            <option value="months">Months</option>
                            <option value="weeks">Weeks</option>
                            <option value="days">Days</option>
                          </Select>
                        </Field>

                        {isTextParam ? (
                          <>
                            <Field label="Min age" hint="In selected unit">
                              <TextInput
                                type="number"
                                placeholder="e.g. 0"
                                value={rangeDraft.min_age}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, min_age: e.target.value }))}
                              />
                            </Field>
                            <Field label="Max age" hint="In selected unit">
                              <TextInput
                                type="number"
                                placeholder="e.g. 120"
                                value={rangeDraft.max_age}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, max_age: e.target.value }))}
                              />
                            </Field>
                            <Field label="Range" hint="Text value">
                              <TextInput
                                value={rangeDraft.text_range}
                                placeholder="e.g. Negative, Trace, 1+"
                                onChange={(e) => setRangeDraft((p) => ({ ...p, text_range: e.target.value }))}
                              />
                            </Field>
                            <Field label="Remarks" hint="Optional">
                              <TextArea
                                rows={2}
                                value={rangeDraft.remarks}
                                placeholder="Optional remarks"
                                onChange={(e) => setRangeDraft((p) => ({ ...p, remarks: e.target.value }))}
                              />
                            </Field>
                          </>
                        ) : (
                          <>
                            <Field label="Min age" hint="In selected unit">
                              <TextInput
                                type="number"
                                placeholder="e.g. 0"
                                value={rangeDraft.min_age}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, min_age: e.target.value }))}
                              />
                            </Field>
                            <Field label="Max age" hint="In selected unit">
                              <TextInput
                                type="number"
                                placeholder="e.g. 120"
                                value={rangeDraft.max_age}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, max_age: e.target.value }))}
                              />
                            </Field>

                            <Field label="Min value" hint="Numeric">
                              <TextInput
                                type="number"
                                value={rangeDraft.min_value}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, min_value: e.target.value }))}
                              />
                            </Field>
                            <Field label="Max value" hint="Numeric">
                              <TextInput
                                type="number"
                                value={rangeDraft.max_value}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, max_value: e.target.value }))}
                              />
                            </Field>

                            <Field label="Critical low" hint="Optional">
                              <TextInput
                                type="number"
                                value={rangeDraft.critical_low}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, critical_low: e.target.value }))}
                              />
                            </Field>
                            <Field label="Critical high" hint="Optional">
                              <TextInput
                                type="number"
                                value={rangeDraft.critical_high}
                                onChange={(e) => setRangeDraft((p) => ({ ...p, critical_high: e.target.value }))}
                              />
                            </Field>

                            <div className="md:col-span-2">
                              <Field label="Remarks" hint="Optional">
                                <TextArea
                                  rows={2}
                                  value={rangeDraft.remarks}
                                  placeholder="e.g. fasting"
                                  onChange={(e) => setRangeDraft((p) => ({ ...p, remarks: e.target.value }))}
                                />
                              </Field>
                            </div>
                          </>
                        )}
                        <div className="md:col-span-2">
                          <Field
                            label="Factors"
                            hint="Optional (one per line key=value)"
                          >
                            <TextArea
                              rows={3}
                              value={rangeDraft.factors}
                              placeholder={"altitude=high\nfasting=true"}
                              onChange={(e) => setRangeDraft((p) => ({ ...p, factors: e.target.value }))}
                            />
                          </Field>
                        </div>
                      </div>

                      <div className="mt-3">
                        <Button
                          type="button"
                          tone="secondary"
                          onClick={addRange}
                          disabled={!selectedParameterId}
                        >
                          <Plus className="h-4 w-4" />
                          Add range
                        </Button>
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-900">Existing ranges</div>
                        <Badge tone="neutral">{ranges.length} total</Badge>
                      </div>
                      {ranges.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                          No ranges yet.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {ranges.map((r) => {
                            const isEditing = rangeEditId === r.id;
                            const d =
                              rangeEditDraftById[r.id] ||
                              {
                                gender: normalizeGender(r.gender),
                                pregnancy_status: normalizePregnancy(r.pregnancy_status),
                                min_age: asString(r.min_age),
                                max_age: asString(r.max_age),
                                min_value: asString(r.min_value),
                                max_value: asString(r.max_value),
                                critical_low: asString(r.critical_low),
                                critical_high: asString(r.critical_high),
                                text_range: asString(r.text_range),
                                remarks: asString(r.remarks),
                                factors: formatFactorsLines(r.factors),
                              };
                            const isTextParam = String(selectedParam?.data_type || "numeric") === "text";

                            return (
                              <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <Badge tone="neutral">range_id: {r.id}</Badge>
                                      <Badge tone="neutral">gender: {normalizeGender(r.gender)}</Badge>
                                      <Badge tone="neutral">pregnancy: {normalizePregnancy(r.pregnancy_status)}</Badge>
                                      <Badge tone="neutral">age: {asString(r.min_age) || "-"} – {asString(r.max_age) || "-"} {r.age_unit || "years"}</Badge>
                                    </div>
                                    {isEditing ? (
                                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                        <Field label="Gender">
                                          <Select
                                            value={d.gender}
                                            onChange={(e) =>
                                              setRangeEditDraftById((prev) => ({
                                                ...prev,
                                                [r.id]: { ...d, gender: e.target.value },
                                              }))
                                            }
                                          >
                                            <option value="both">Any</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                          </Select>
                                        </Field>
                                        <Field label="Pregnancy">
                                          <Select
                                            value={d.pregnancy_status}
                                            onChange={(e) =>
                                              setRangeEditDraftById((prev) => ({
                                                ...prev,
                                                [r.id]: { ...d, pregnancy_status: e.target.value },
                                              }))
                                            }
                                          >
                                            <option value="both">Any</option>
                                            <option value="pregnant">Pregnant</option>
                                            <option value="not_pregnant">Not pregnant</option>
                                          </Select>
                                        </Field>

                                        <Field label="Age unit">
                                          <Select
                                            value={d.age_unit}
                                            onChange={(e) =>
                                              setRangeEditDraftById((prev) => ({
                                                ...prev,
                                                [r.id]: { ...d, age_unit: e.target.value },
                                              }))
                                            }
                                          >
                                            <option value="years">Years</option>
                                            <option value="months">Months</option>
                                            <option value="weeks">Weeks</option>
                                            <option value="days">Days</option>
                                          </Select>
                                        </Field>

                                        {isTextParam ? (
                                          <>
                                            <Field label="Range">
                                              <TextInput
                                                value={d.text_range}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, text_range: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>
                                            <Field label="Remarks" hint="Optional">
                                              <TextInput
                                                value={d.remarks}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, remarks: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>
                                          </>
                                        ) : (
                                          <>
                                            <Field label="Min age" hint="In selected unit">
                                              <TextInput
                                                type="number"
                                                value={d.min_age}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, min_age: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>
                                            <Field label="Max age" hint="In selected unit">
                                              <TextInput
                                                type="number"
                                                value={d.max_age}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, max_age: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>

                                            <Field label="Min value">
                                              <TextInput
                                                type="number"
                                                value={d.min_value}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, min_value: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>
                                            <Field label="Max value">
                                              <TextInput
                                                type="number"
                                                value={d.max_value}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, max_value: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>

                                            <Field label="Critical low" hint="Optional">
                                              <TextInput
                                                type="number"
                                                value={d.critical_low}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, critical_low: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>
                                            <Field label="Critical high" hint="Optional">
                                              <TextInput
                                                type="number"
                                                value={d.critical_high}
                                                onChange={(e) =>
                                                  setRangeEditDraftById((prev) => ({
                                                    ...prev,
                                                    [r.id]: { ...d, critical_high: e.target.value },
                                                  }))
                                                }
                                              />
                                            </Field>

                                            <div className="md:col-span-2">
                                              <Field label="Remarks" hint="Optional">
                                                <TextInput
                                                  value={d.remarks}
                                                  onChange={(e) =>
                                                    setRangeEditDraftById((prev) => ({
                                                      ...prev,
                                                      [r.id]: { ...d, remarks: e.target.value },
                                                    }))
                                                  }
                                                />
                                              </Field>
                                            </div>
                                          </>
                                        )}

                                        <div className="md:col-span-2">
                                          <Field label="Factors" hint='Optional (one per line key=value)'>
                                            <TextArea
                                              rows={3}
                                              value={d.factors}
                                              onChange={(e) =>
                                                setRangeEditDraftById((prev) => ({
                                                  ...prev,
                                                  [r.id]: { ...d, factors: e.target.value },
                                                }))
                                              }
                                            />
                                          </Field>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                                          <div className="text-xs text-slate-500">{isTextParam ? "Range" : "Normal"}</div>
                                          <div className="mt-1 text-sm font-semibold text-slate-900">
                                            {isTextParam
                                              ? (asString(r.text_range) || "-")
                                              : `${asString(r.min_value) || "-"} – ${asString(r.max_value) || "-"}`}
                                          </div>
                                        </div>
                                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                                          <div className="text-xs text-slate-500">{isTextParam ? "Remarks" : "Critical"}</div>
                                          <div className="mt-1 text-sm font-semibold text-slate-900">
                                            {isTextParam
                                              ? (asString(r.remarks) || "-")
                                              : `${asString(r.critical_low) || "-"} – ${asString(r.critical_high) || "-"}`}
                                          </div>
                                        </div>
                                        <div className="md:col-span-2 rounded-xl bg-slate-50 px-3 py-2">
                                          <div className="text-xs text-slate-500">Remarks</div>
                                          <div className="mt-1 text-sm text-slate-900">{r.remarks ?? "-"}</div>
                                        </div>
                                        <div className="md:col-span-2 rounded-xl bg-slate-50 px-3 py-2">
                                          <div className="text-xs text-slate-500">Factors</div>
                                          <div className="mt-1 whitespace-pre-wrap text-sm text-slate-900">
                                            {formatFactorsLines(r.factors) || "-"}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex shrink-0 items-center gap-2">
                                    {isEditing ? (
                                      <>
                                        <Button type="button" size="sm" onClick={() => saveEditRange(r)}>
                                          <Save className="h-4 w-4" />
                                          Save
                                        </Button>
                                        <Button type="button" tone="soft" size="sm" onClick={cancelEditRange}>
                                          <X className="h-4 w-4" />
                                          Cancel
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button type="button" tone="soft" size="sm" onClick={() => beginEditRange(r)}>
                                          <Pencil className="h-4 w-4" />
                                          Edit
                                        </Button>
                                        <Button type="button" tone="danger" size="sm" onClick={() => removeRangeWithConfirm(r.id, r.parameter_id)}>
                                          <Trash2 className="h-4 w-4" />
                                          Delete
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  const renderQuestions = () => {
    if (!draft.id) {
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>
              <div className="font-semibold">Save the draft first</div>
              <div className="mt-1 text-xs text-amber-800">
                Dynamic questions are attached to a specific test.
              </div>
            </div>
          </div>
        </div>
      );
    }

    const beginEditQuestion = (q) => {
      setEditingQuestionId(q.id);
      setQuestionEditDraftById((prev) => ({
        ...prev,
        [q.id]: {
          question_text: asString(q.question_text),
          field_type: asString(q.field_type || "text"),
          is_required: Boolean(q.is_required) ? 1 : 0,
          optionsText: Array.isArray(q.options) ? q.options.join("\n") : "",
        },
      }));
    };

    const cancelEditQuestion = () => setEditingQuestionId(null);

    const saveEditQuestion = async (q) => {
      const d = questionEditDraftById[q.id];
      if (!d) return;
      const dbFieldType = mapUiFieldTypeToDb(d.field_type);
      const optionsArray = parseOptionsLines(d.optionsText);
      if (needsOptionsForDbFieldType(dbFieldType) && optionsArray.length === 0) {
        // eslint-disable-next-line no-alert
        toast.error("Options are required (enter one option per line).");
        return;
      }

      await updateQuestionInline(q, {
        question_text: d.question_text,
        field_type: d.field_type,
        is_required: d.is_required ? 1 : 0,
        options: needsOptionsForDbFieldType(dbFieldType) ? optionsArray : [],
      });
      setEditingQuestionId(null);
    };

    return (
      <div className="space-y-5">
        <Card>
          <CardHeader
            title="Add a dynamic question"
            subtitle="Shown when ordering the test (optional)"
          />
          <CardBody>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Question text">
                <TextInput
                  placeholder="e.g. Was the patient fasting?"
                  value={questionDraft.question_text}
                  onChange={(e) =>
                    setQuestionDraft((p) => ({ ...p, question_text: e.target.value }))
                  }
                />
              </Field>
              <Field label="Field type">
                <Select
                  value={questionDraft.field_type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    const nextDbType = mapUiFieldTypeToDb(nextType);
                    setQuestionDraft((p) => ({
                      ...p,
                      field_type: nextType,
                      options: needsOptionsForDbFieldType(nextDbType) ? p.options : "",
                    }));
                  }}
                >
                  {FIELD_TYPE_UI_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    checked={Boolean(questionDraft.is_required)}
                    onChange={(e) =>
                      setQuestionDraft((p) => ({
                        ...p,
                        is_required: e.target.checked ? 1 : 0,
                      }))
                    }
                  />
                  <span className="text-sm font-medium text-slate-900">Required</span>
                  <span className="text-xs text-slate-500">(must be answered)</span>
                </label>
              </div>

              {needsOptionsForDbFieldType(mapUiFieldTypeToDb(questionDraft.field_type)) ? (
                <div className="md:col-span-2">
                  <Field label="Options" hint="One per line">
                    <TextArea
                      rows={3}
                      placeholder={"Yes\nNo"}
                      value={questionDraft.options}
                      onChange={(e) =>
                        setQuestionDraft((p) => ({ ...p, options: e.target.value }))
                      }
                    />
                  </Field>
                </div>
              ) : null}
            </div>

            <div className="mt-4">
              <Button type="button" tone="secondary" onClick={addQuestion}>
                <Plus className="h-4 w-4" />
                Add question
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Existing questions"
            subtitle={questions.length ? `${questions.length} configured` : "None yet"}
          />
          <CardBody>
            {questions.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                No questions yet — add one above.
              </div>
            ) : (
              <div className="space-y-2">
                {questions.map((q) => {
                  const isEditing = editingQuestionId === q.id;
                  const d =
                    questionEditDraftById[q.id] ||
                    {
                      question_text: asString(q.question_text),
                      field_type: asString(q.field_type || "text"),
                      is_required: Boolean(q.is_required) ? 1 : 0,
                      optionsText: Array.isArray(q.options) ? q.options.join("\n") : "",
                    };

                  return (
                    <div key={q.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge tone="neutral">question_id: {q.id}</Badge>
                            <Badge tone={q.is_required ? "warning" : "neutral"}>
                              {q.is_required ? "Required" : "Optional"}
                            </Badge>
                            <Badge tone="info">{q.field_type || "text"}</Badge>
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Field label="Text">
                              {isEditing ? (
                                <TextInput
                                  value={d.question_text}
                                  onChange={(e) =>
                                    setQuestionEditDraftById((prev) => ({
                                      ...prev,
                                      [q.id]: { ...d, question_text: e.target.value },
                                    }))
                                  }
                                />
                              ) : (
                                <div className="text-sm font-semibold text-slate-900">{q.question_text}</div>
                              )}
                            </Field>
                            <Field label="Type">
                              {isEditing ? (
                                <Select
                                  value={d.field_type}
                                  onChange={(e) => {
                                    const nextType = e.target.value;
                                    const dbType = mapUiFieldTypeToDb(nextType);
                                    setQuestionEditDraftById((prev) => ({
                                      ...prev,
                                      [q.id]: {
                                        ...d,
                                        field_type: nextType,
                                        optionsText: needsOptionsForDbFieldType(dbType) ? d.optionsText : "",
                                      },
                                    }));
                                  }}
                                >
                                  <option value="text">text</option>
                                  <option value="dropdown">dropdown</option>
                                  <option value="radio">radio</option>
                                  <option value="checkbox">checkbox</option>
                                  <option value="number">number</option>
                                </Select>
                              ) : (
                                <div className="text-sm text-slate-700">{q.field_type || "text"}</div>
                              )}
                            </Field>

                            {isEditing ? (
                              <div className="md:col-span-2">
                                <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    checked={Boolean(d.is_required)}
                                    onChange={(e) =>
                                      setQuestionEditDraftById((prev) => ({
                                        ...prev,
                                        [q.id]: { ...d, is_required: e.target.checked ? 1 : 0 },
                                      }))
                                    }
                                  />
                                  <span className="text-sm font-medium text-slate-900">Required</span>
                                </label>
                              </div>
                            ) : null}

                            {isEditing && needsOptionsForDbFieldType(mapUiFieldTypeToDb(d.field_type)) ? (
                              <div className="md:col-span-2">
                                <Field label="Options" hint="One per line">
                                  <TextArea
                                    rows={3}
                                    value={d.optionsText}
                                    onChange={(e) =>
                                      setQuestionEditDraftById((prev) => ({
                                        ...prev,
                                        [q.id]: { ...d, optionsText: e.target.value },
                                      }))
                                    }
                                  />
                                </Field>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button type="button" size="sm" onClick={() => saveEditQuestion(q)}>
                                <Save className="h-4 w-4" />
                                Save
                              </Button>
                              <Button type="button" tone="soft" size="sm" onClick={cancelEditQuestion}>
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button type="button" tone="soft" size="sm" onClick={() => beginEditQuestion(q)}>
                                <Pencil className="h-4 w-4" />
                                Edit
                              </Button>
                              <Button type="button" tone="danger" size="sm" onClick={() => removeQuestionWithConfirm(q.id)}>
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    );
  };

  const renderConfirm = () => {
    if (publishSuccess) {
      return (
        <div className="space-y-5">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <div className="flex items-start gap-4">
              <span className="mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white ring-1 ring-emerald-200">
                <CheckCircle2 className="h-6 w-6 text-emerald-700" />
              </span>
              <div className="min-w-0">
                <div className="text-base font-semibold text-emerald-950">
                  Test activated successfully
                </div>
                <div className="mt-1 text-sm text-emerald-900">
                  <span className="font-semibold">{draft.name || "This test"}</span> is now orderable.
                </div>
                <div className="mt-2 text-xs text-emerald-800">
                  You can review parameters and reference ranges any time.
                </div>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader title="Summary" subtitle="Published configuration" />
            <CardBody>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-medium text-slate-600">Test ID</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{draft.id || "-"}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-medium text-slate-600">Code</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{draft.code || "-"}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-medium text-slate-600">Status</div>
                  <div className="mt-1 inline-flex">
                    <Badge tone="success">Active</Badge>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      );
    }

    const categoryName =
      testCategories.find((c) => String(c.id) === String(draft.category_id))
        ?.name ||
      currentTest?.category_name ||
      "";

    const effectiveTestId = draft.id || currentTest?.id;
    const confirmSampleTypes = effectiveTestId
      ? testSampleTypesByTest[effectiveTestId] || []
      : [];
    const confirmParameters = effectiveTestId ? parametersByTest[effectiveTestId] || [] : [];

    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white ring-1 ring-amber-200">
              <AlertTriangle className="h-5 w-5 text-amber-900" />
            </span>
            <div>
              <div className="text-sm font-semibold text-amber-950">Activation warning</div>
              <div className="mt-0.5 text-xs text-amber-900">
                Once activated, this test becomes orderable. Changes may affect existing results and reporting.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Test summary" subtitle="Review before publishing" />
            <CardBody>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Category</span>
                  <span className="font-semibold text-slate-900">{categoryName || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Test</span>
                  <span className="font-semibold text-slate-900">{draft.name || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Code</span>
                  <span className="font-semibold text-slate-900">{draft.code || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Price</span>
                  <span className="font-semibold text-slate-900">{draft.price || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Processing time</span>
                  <span className="font-semibold text-slate-900">{draft.processing_time || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Testing modes</span>
                  <span className="font-semibold text-slate-900">{draft.testing_modes || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Sample volume</span>
                  <span className="font-semibold text-slate-900">{draft.sample_volume || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Linear range</span>
                  <span className="font-semibold text-slate-900">{draft.linear_range || "-"}</span>
                </div>
                <div className="pt-2">
                  <div className="text-xs font-medium text-slate-600">Description</div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-slate-900">
                    {draft.description ? String(draft.description) : "-"}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Readiness" subtitle="Checks based on LIS rules" />
            <CardBody>
              {confirmationIssues.length === 0 ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" />
                    <div>
                      <div className="text-sm font-semibold text-emerald-900">All checks passed</div>
                      <div className="mt-0.5 text-xs text-emerald-800">
                        You can activate this test.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-rose-700" />
                    <div>
                      <div className="text-sm font-semibold text-rose-900">Action required</div>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-rose-900">
                        {confirmationIssues.map((i, idx) => (
                          <li key={idx}>{i}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader title="Configuration overview" subtitle="Sample types → parameters → ranges" />
          <CardBody>
            {confirmPrefetching ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading sample types, parameters, and ranges…
                </div>
              </div>
            ) : null}

            <div className="space-y-3">
              {confirmSampleTypes.length === 0 && !confirmPrefetching ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  No sample types found for this test yet.
                </div>
              ) : null}

              {confirmSampleTypes.map((tst) => {
                const ps = confirmParameters.filter(
                  (p) => parameterMatchesTst(p, tst.id, effectiveTestId)
                );
                const tubeIds = tubeTypeIdsBySampleTypeId[tst.sample_type_id] || [];
                const tubeNames = (tubeIds || [])
                  .map(
                    (id) => tubeTypes.find((tt) => String(tt.id) === String(id))?.name
                  )
                  .filter(Boolean);
                return (
                  <div key={tst.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{tst.sample_type_name}</div>
                      <Badge tone="neutral">{ps.length} parameter(s)</Badge>
                    </div>

                    <div className="mt-2 text-xs text-slate-600">
                      <span className="font-medium">Tubes:</span>{" "}
                      {tubeNames.length > 0 ? tubeNames.join(", ") : "—"}
                    </div>

                    {ps.length === 0 ? (
                      <div className="mt-2 text-sm text-slate-500">No parameters</div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {ps.map((p) => {
                          const rs = rangesByParameter[p.id] || [];
                          return (
                            <div key={p.id} className="flex flex-col gap-1 rounded-xl bg-slate-50 px-3 py-2">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-slate-900">
                                  {p.parameter_name || p.name}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge tone={(p.data_type || "numeric") === "numeric" ? "info" : "neutral"}>
                                    {p.data_type || "numeric"}
                                  </Badge>
                                  <Badge tone="neutral">{rs.length} range(s)</Badge>
                                </div>
                              </div>
                              <div className="text-xs text-slate-500">Unit: {p.unit || "-"}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Publish" subtitle="Final action" />
          <CardBody>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-slate-700">
                Activating sets <span className="font-semibold">is_active=1</span>. Earlier steps save as draft.
              </div>
              <Button
                type="button"
                tone={confirmationIssues.length === 0 ? "primary" : "soft"}
                disabled={confirmationIssues.length !== 0}
                onClick={() => {
                  openConfirmDialog({
                    title: "Activate test",
                    message: `Activate '${draft.name || "this test"}'? It will become orderable.`,
                    confirmText: "Activate",
                    confirmTone: "primary",
                    onConfirm: confirmAndActivate,
                  });
                }}
                className="w-full md:w-auto"
              >
                <CheckCircle2 className="h-4 w-4" />
                Confirm & Activate
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep.id) {
      case "basics":
        return renderBasics();
      case "parameters":
        return renderParameters();
      case "ranges":
        return renderRanges();
      case "questions":
        return renderQuestions();
      case "confirm":
        return renderConfirm();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-lg font-bold text-slate-900">Test Management</div>
            <div className="mt-0.5 text-xs text-slate-500">
              Configure tests safely: sample types → parameters → ranges
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button type="button" tone="primary" onClick={startNew}>
              <Plus className="h-4 w-4" />
              New test
            </Button>
            <div className="relative w-full sm:w-80">
              <TextInput
                value={search}
                placeholder="Search tests by name or code"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="w-full">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader
                title="Tests"
                subtitle="Select a test to edit"
                right={
                  loading ? (
                    <span className="inline-flex items-center gap-2 text-xs text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading
                    </span>
                  ) : null
                }
              />
              <CardBody>
                {filteredTests.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    No tests found.
                  </div>
                ) : (
                  <div className="max-h-[calc(100vh-220px)] space-y-2 overflow-auto pr-1">
                    {filteredTests.map((t) => {
                      const selected = selectedTestId === t.id;
                      return (
                        <div
                          key={t.id}
                          className={cx(
                            "w-full rounded-2xl border bg-white px-4 py-3 transition",
                            selected
                              ? "border-indigo-200 bg-indigo-50"
                              : "border-slate-200 hover:bg-slate-50"
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-900">{t.name}</div>
                              <div className="mt-0.5 text-xs text-slate-500">{t.category_name || ""}</div>
                            </div>

                            <div className="flex flex-col items-end gap-2">
                              <div className="flex items-center gap-2">
                                <Badge tone="neutral">{t.code}</Badge>
                                {t.is_active ? (
                                  <Badge tone="success">Active</Badge>
                                ) : (
                                  <Badge tone="warning">Draft</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="relative">
                                  <Button
                                    type="button"
                                    tone="soft"
                                    size="sm"
                                    aria-label="Actions"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const nextId = openActionsForTestId === t.id ? null : t.id;
                                      setOpenActionsForTestId(nextId);
                                      if (nextId) {
                                        setDropdownPosition({
                                          top: rect.bottom + 8,
                                          right: window.innerWidth - rect.right,
                                        });
                                      }
                                    }}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Right-side drawer for View/Edit */}
        {drawerOpen ? (
          <div className="fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/30"
              aria-label="Close"
              onClick={() => setDrawerOpen(false)}
            />

            <div className="absolute right-0 top-0 h-full w-full max-w-5xl border-l border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {drawerMode === "view" ? "View Test" : "Edit Test"}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {drawerMode === "view"
                      ? "Read-only overview"
                      : draft.id
                        ? `Editing test_id: ${draft.id}`
                        : "Create a new test"}
                  </div>
                </div>

                <Button type="button" tone="soft" size="sm" onClick={() => setDrawerOpen(false)}>
                  <X className="h-4 w-4" />
                  Close
                </Button>
              </div>

              <div className="h-[calc(100%-52px)] overflow-auto p-4 md:p-6">
                {drawerMode === "view" ? (
                  <Card>
                    <CardHeader
                      title={tests.find((t) => t.id === drawerTestId)?.name || "Test"}
                      subtitle={
                        tests.find((t) => t.id === drawerTestId)
                          ? `Code: ${tests.find((t) => t.id === drawerTestId)?.code || "-"}`
                          : ""
                      }
                      right={
                        drawerTestId ? (
                          <div className="relative">
                            <Button
                              type="button"
                              tone="soft"
                              size="sm"
                              aria-label="Actions"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const nextId = openActionsForTestId === drawerTestId ? null : drawerTestId;
                                    setOpenActionsForTestId(nextId);
                                    if (nextId) {
                                      setDropdownPosition({
                                        top: rect.bottom + 8,
                                        right: window.innerWidth - rect.right,
                                      });
                                    }
                                  }}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : null}
                      />
                      <CardBody>
                      {!drawerTestId ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                          Select a test to view.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Step 1: Basics */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="text-sm font-semibold text-slate-900">Test basics</div>
                            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Category</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewTest?.category_name || "-"}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Status</div>
                                <div className="mt-2">
                                  {viewTest?.is_active ? (
                                    <Badge tone="success">Active</Badge>
                                  ) : (
                                    <Badge tone="warning">Draft</Badge>
                                  )}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Tubes (from sample types)</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewSampleTypes.length === 0
                                    ? "-"
                                    : viewSampleTypes
                                        .map((tst) => {
                                          const tubeIds =
                                            tubeTypeIdsBySampleTypeId[tst.sample_type_id] || [];
                                          const tubeNames = (tubeIds || [])
                                            .map(
                                              (id) =>
                                                tubeTypes.find((tt) => String(tt.id) === String(id))
                                                  ?.name
                                            )
                                            .filter(Boolean);
                                          return `${tst.sample_type_name}: ${
                                            tubeNames.length > 0 ? tubeNames.join(", ") : "—"
                                          }`;
                                        })
                                        .join(" • ")}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Price</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewTest?.price !== null && viewTest?.price !== undefined && viewTest?.price !== ""
                                    ? String(viewTest.price)
                                    : "-"}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Processing time</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewTest?.processing_time || "-"}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Testing modes</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewTest?.testing_modes || "-"}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Sample volume</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewTest?.sample_volume || "-"}
                                </div>
                              </div>

                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-500">Linear range</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {viewTest?.linear_range || "-"}
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2">
                              <div className="text-xs text-slate-500">Description</div>
                              <div className="mt-1 text-sm text-slate-900">
                                {viewTest?.description || "-"}
                              </div>
                            </div>
                          </div>

                          {/* Step 2: Sample Types */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-semibold text-slate-900">Sample types</div>
                              <Badge tone="neutral">{viewSampleTypes.length}</Badge>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {viewSampleTypes.length === 0 ? (
                                <div className="text-sm text-slate-600">No sample types assigned.</div>
                              ) : (
                                viewSampleTypes.map((row) => (
                                  <Badge key={row.id} tone="info">
                                    {row.sample_type_name}
                                  </Badge>
                                ))
                              )}
                            </div>
                          </div>

                          {/* Step 3: Parameters */}
                          {/* <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-semibold text-slate-900">Parameters</div>
                              <Badge tone="neutral">{viewParameters.length}</Badge>
                            </div>
                            <div className="mt-3 space-y-3">
                              {viewSampleTypes.length === 0 ? (
                                <div className="text-sm text-slate-600">Assign sample types to view parameters.</div>
                              ) : (
                                viewSampleTypes.map((tst) => {
                                  const ps = viewParameters.filter(
                                    (p) => parameterMatchesTst(p, tst.id, drawerTestId)
                                  );
                                  return (
                                    <div key={tst.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-semibold text-slate-900">{tst.sample_type_name}</div>
                                        <Badge tone="neutral">{ps.length} params</Badge>
                                      </div>
                                      <div className="mt-2 space-y-2">
                                        {ps.length === 0 ? (
                                          <div className="text-sm text-slate-600">No parameters added.</div>
                                        ) : (
                                          ps.map((p) => (
                                            <div key={p.id} className="flex flex-col gap-1 rounded-xl bg-white px-3 py-2">
                                              <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                  <div className="truncate text-sm font-semibold text-slate-900">
                                                    {p.parameter_name || p.name || "(unnamed parameter)"}
                                                  </div>
                                                  <div className="mt-0.5 text-xs text-slate-500">
                                                    unit: {p.unit || "-"}
                                                  </div>
                                                </div>
                                                <div className="shrink-0">
                                                  <Badge tone="neutral">{p.data_type || "numeric"}</Badge>
                                                </div>
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div> */}

                          {/* Step 4: Reference Ranges */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="text-sm font-semibold text-slate-900">Reference ranges</div>
                            <div className="mt-3 space-y-3">
                              {viewSampleTypes.length === 0 ? (
                                <div className="text-sm text-slate-600">Assign sample types to view ranges.</div>
                              ) : (
                                viewSampleTypes.map((tst) => {
                                  const ps = viewParameters.filter(
                                    (p) => parameterMatchesTst(p, tst.id, drawerTestId)
                                  );
                                  const typed = ps;
                                  const numericCount = typed.filter(
                                    (p) => (p.data_type || "numeric") === "numeric"
                                  ).length;
                                  const textCount = typed.filter(
                                    (p) => String(p.data_type || "numeric").toLowerCase() === "text"
                                  ).length;
                                  return (
                                    <div key={tst.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-semibold text-slate-900">{tst.sample_type_name}</div>
                                        <Badge tone="neutral">{numericCount} numeric / {textCount} text</Badge>
                                      </div>

                                      <div className="mt-2 space-y-2">
                                        {typed.length === 0 ? (
                                          <div className="text-sm text-slate-600">No parameters.</div>
                                        ) : (
                                          typed.map((p) => {
                                            const rs = rangesByParameter[p.id] || [];
                                            const isText = String(p.data_type || "numeric").toLowerCase() === "text";
                                            return (
                                              <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                                                <div className="flex items-center justify-between gap-3">
                                                  <div className="text-sm font-semibold text-slate-900">
                                                    {p.parameter_name || p.name || "(unnamed parameter)"}
                                                  </div>
                                                  <Badge tone="neutral">{isText ? "text" : "numeric"}</Badge>
                                                  <Badge tone={rs.length > 0 ? "success" : "warning"}>
                                                    {rs.length} ranges
                                                  </Badge>
                                                </div>
                                                <div className="mt-2 space-y-2">
                                                  {rs.length === 0 ? (
                                                    <div className="text-sm text-slate-600">No ranges configured.</div>
                                                  ) : (
                                                    rs.map((r) => (
                                                      <div key={r.id} className="rounded-xl bg-slate-50 px-3 py-2">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                          <Badge tone="neutral">gender: {r.gender || "both"}</Badge>
                                                          <Badge tone="neutral">pregnancy: {r.pregnancy_status || "both"}</Badge>
                                                          <Badge tone="neutral">
                                                            age: {r.min_age ?? "-"} – {r.max_age ?? "-"} {r.age_unit || "years"}
                                                          </Badge>
                                                        </div>
                                                        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                                                          <div className="rounded-lg bg-white px-2 py-1">
                                                            <div className="text-[11px] text-slate-500">{isText ? "Range" : "Normal"}</div>
                                                            <div className="text-sm font-semibold text-slate-900">
                                                              {isText
                                                                ? (r.text_range ?? "-")
                                                                : `${r.min_value ?? "-"} – ${r.max_value ?? "-"}`}
                                                            </div>
                                                          </div>
                                                          <div className="rounded-lg bg-white px-2 py-1">
                                                            <div className="text-[11px] text-slate-500">{isText ? "Remarks" : "Critical"}</div>
                                                            <div className="text-sm font-semibold text-slate-900">
                                                              {isText
                                                                ? (r.remarks ?? "-")
                                                                : `${r.critical_low ?? "-"} – ${r.critical_high ?? "-"}`}
                                                            </div>
                                                          </div>
                                                        </div>
                                                        {r.remarks ? (
                                                          <div className="mt-2 text-xs text-slate-700">
                                                            <span className="font-medium">Remarks:</span> {String(r.remarks)}
                                                          </div>
                                                        ) : null}
                                                        {r.factors ? (
                                                          <div className="mt-1 text-xs text-slate-700">
                                                            <span className="font-medium">Factors:</span>{" "}
                                                            {typeof r.factors === "object"
                                                              ? JSON.stringify(r.factors)
                                                              : String(r.factors)}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    ))
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          {/* Step 5: Questions */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-semibold text-slate-900">Dynamic questions</div>
                              <Badge tone="neutral">{viewQuestions.length}</Badge>
                            </div>
                            <div className="mt-3 space-y-2">
                              {viewQuestions.length === 0 ? (
                                <div className="text-sm text-slate-600">No questions added.</div>
                              ) : (
                                viewQuestions.map((q) => (
                                  <div key={q.id} className="rounded-xl bg-slate-50 px-3 py-2">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-900">
                                          {q.question_text || "(empty question)"}
                                        </div>
                                        <div className="mt-0.5 text-xs text-slate-500">
                                          type: {q.field_type || "text"}
                                        </div>
                                      </div>
                                      <div className="shrink-0">
                                        {q.is_required ? (
                                          <Badge tone="warning">Required</Badge>
                                        ) : (
                                          <Badge tone="neutral">Optional</Badge>
                                        )}
                                      </div>
                                    </div>
                                    {Array.isArray(q.options) && q.options.length > 0 ? (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {q.options.map((opt, idx) => (
                                          <Badge key={idx} tone="neutral">
                                            {String(opt)}
                                          </Badge>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>

                          {/* Step 6: Readiness */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="text-sm font-semibold text-slate-900">Readiness</div>
                            <div className="mt-3">
                              {viewIssues.length === 0 ? (
                                <div className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
                                  <CheckCircle2 className="h-4 w-4" />
                                  Looks ready (no missing sample types/parameters/ranges).
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {viewIssues.map((msg, idx) => (
                                    <div key={idx} className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
                                      <AlertTriangle className="mt-0.5 h-4 w-4" />
                                      <div className="min-w-0">{msg}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader
                      title="Wizard"
                      subtitle={
                        draft.id
                          ? `Editing test_id: ${draft.id}`
                          : "Create a new test or select an existing one"
                      }
                      right={
                        draft.id ? (
                          <div className="flex items-center gap-2">
                            {draft.is_active ? (
                              <Badge tone="success">Active</Badge>
                            ) : (
                              <Badge tone="warning">Draft</Badge>
                            )}
                          </div>
                        ) : null
                      }
                    />

                    <CardBody>
                      <Stepper
                        stepIndex={stepIndex}
                        steps={steps}
                        canGoToIndex={canGoToIndex}
                        onStepClick={setStepIndex}
                        summary={stepSummary}
                      />

                      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-sm font-semibold text-slate-900">
                          {steps[stepIndex]?.label}
                        </div>
                        <Badge tone="neutral">
                          Step {stepIndex + 1} / {steps.length}
                        </Badge>
                      </div>

                      <div className="mt-4 min-h-[60vh] lg:min-h-[700px] flex flex-col">
                        <div className="flex-1 min-h-0">{renderStepContent()}</div>

                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <Button
                            type="button"
                            tone="soft"
                            onClick={goBack}
                            disabled={stepIndex === 0}
                          >
                            Back
                          </Button>

                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            {!canGoToNext() ? (
                              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                Complete required fields to continue.
                              </div>
                            ) : null}
                            <Button
                              type="button"
                              tone={canGoToNext() ? "secondary" : "soft"}
                              onClick={goNext}
                              disabled={!canGoToNext() || (currentStep.id === "confirm" && publishSuccess)}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {confirmDialog.open ? (
          <div className="fixed inset-0 z-[60]">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/40"
              aria-label="Close"
              onClick={closeConfirmDialog}
            />
            <div className="absolute left-1/2 top-1/2 w-[min(520px,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 p-5">
                  <div className="text-sm font-semibold text-slate-900">{confirmDialog.title}</div>
                  <div className="mt-1 text-xs text-slate-500">Please confirm the action</div>
                </div>
                <div className="p-5">
                  <div className="text-sm text-slate-700">{confirmDialog.message}</div>
                  <div className="mt-4 flex items-center justify-end gap-2">
                    <Button type="button" tone="soft" onClick={() => closeConfirmDialog(true)} disabled={confirmDialogBusy}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      tone={confirmDialog.confirmTone}
                      disabled={confirmDialogBusy}
                      onClick={async () => {
                        const fn = confirmDialogActionRef.current;
                        if (!fn) {
                          closeConfirmDialog(true);
                          return;
                        }
                        try {
                          setConfirmDialogBusy(true);
                          await fn();
                          closeConfirmDialog(false);
                        } finally {
                          setConfirmDialogBusy(false);
                        }
                      }}
                    >
                      {confirmDialog.confirmText}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {openActionsForTestId && dropdownPosition && activeTestForMenu ? (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpenActionsForTestId(null)}
          >
            <div
              className="absolute z-50 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
              onClick={(e) => e.stopPropagation()}
            >
              {openActionsForTestId === drawerTestId ? (
                <>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                    onClick={() => {
                      setOpenActionsForTestId(null);
                      openExisting(drawerTestId, 0);
                    }}
                  >
                    Edit
                  </button>
                  {viewTest?.is_active ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50"
                      onClick={() => {
                        setOpenActionsForTestId(null);
                        deactivateTestWithConfirm(drawerTestId);
                      }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                      onClick={() => {
                        setOpenActionsForTestId(null);
                        const confirmIdx = stepsForTestingMode(viewTest?.testing_mode).findIndex(
                          (s) => s.id === "confirm"
                        );
                        openExisting(drawerTestId, confirmIdx >= 0 ? confirmIdx : 0);
                      }}
                    >
                      Activate
                    </button>
                  )}
                  <div className="h-px bg-slate-100" />
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50"
                    onClick={() => {
                      setOpenActionsForTestId(null);
                      deleteTestWithConfirm(drawerTestId);
                    }}
                  >
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                    onClick={() => {
                      setOpenActionsForTestId(null);
                      openView(activeTestForMenu.id);
                    }}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                    onClick={() => {
                      setOpenActionsForTestId(null);
                      openExisting(activeTestForMenu.id, 0);
                    }}
                  >
                    Edit
                  </button>
                  {!activeTestForMenu.is_active ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                      onClick={() => {
                        setOpenActionsForTestId(null);
                        const confirmIdx = stepsForTestingMode(activeTestForMenu.testing_mode).findIndex(
                          (s) => s.id === "confirm"
                        );
                        openExisting(activeTestForMenu.id, confirmIdx >= 0 ? confirmIdx : 0);
                      }}
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50"
                      onClick={() => {
                        setOpenActionsForTestId(null);
                        deactivateTestWithConfirm(activeTestForMenu.id);
                      }}
                    >
                      Deactivate
                    </button>
                  )}
                  <div className="h-px bg-slate-100" />
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50"
                    onClick={() => {
                      setOpenActionsForTestId(null);
                      deleteTestWithConfirm(activeTestForMenu.id);
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TestManagement;
